/**
 * The game view: Three.js scene, third-person camera, client-side prediction
 * for the own character and 133 ms interpolation for everything else
 * (AD-026, networking.md "Movement").
 *
 * Prediction uses the exact shared movement code the server runs; on every
 * snapshot the client rewinds to the authoritative state, replays
 * unacknowledged inputs, and smooths small corrections over ~150 ms (large
 * divergence snaps). The measured reconciliation error is surfaced in the
 * HUD — the Phase 3 proof reads the same number.
 */

import * as THREE from 'three';

import {
  DEFAULT_APPEARANCE,
  DODGE_COOLDOWN_MS,
  INTERP_DELAY_MS,
  InterpolationBuffer,
  MOBILITY_DASH_MAX_M,
  TICK_MS,
  TICK_RATE,
  animFor,
  clampToZone,
  clientBiomeTierOf,
  clientGenerationOf,
  clientTerrainIdOf,
  clientTerrainSetOf,
  createMovementState,
  interpDelayMsFor,
  offerNpcSet,
  type TurnInQuestLike,
  readyTurnInNpcs,
  stepMovement,
} from '@great-cloud/shared/client';
import type { DungeonPlan } from '@great-cloud/procedural-dungeons';
import type {
  CharacterAppearance,
  DungeonResultPayload,
  EntityDelta,
  EntityState,
  GameEvent,
  HelloAckPayload,
  InputPayload,
  MovementState,
  NodeWire,
  NpcWire,
  SnapshotPayload,
  TelegraphShape,
  WorldCollision,
  WorldDescriptor,
  ZoneBounds,
} from '@great-cloud/shared/client';

import type { ConnectionPhase, GameConnection } from '../net/connection.js';
import { assetHttpBase } from '../net/platform.js';
import { InputState } from './input.js';
import { CAMERA_BOUNDS, OrbitCamera } from './camera.js';
import {
  CAPTURE_KEYS,
  captureEquipmentHidden,
  captureMarkersHidden,
  capturedTexturesPreference,
  presetById,
  readFlag,
  slowOrbitYawDelta,
} from './capture.js';
import {
  type FramingRect,
  type FramingView,
  type OrbitFraming,
  framingStale,
  resolveOrbitFraming,
  stepFramedOrbit,
  voidExposure,
} from './captureFraming.js';
import {
  characterReadiness,
  countPlaceholderBodies,
  markPlaceholderBody,
} from './placeholderBodies.js';
import { AssetManifest, createThreeGltfLoader, disposeObject3D } from './assets/index.js';
import type { CreatedRenderer, GameRenderer, RendererKind, RendererLossInfo } from './renderer.js';
import { watchRendererLoss } from './renderer.js';
import {
  PostProcessingStack,
  SceneBuilder,
  createBrowserGroundTextureLoad,
  resolveQualityProfile,
  setActiveQualityProfile,
} from './scene/index.js';
import type { PostFxLike, QualityProfile, RenderTier, SceneContentLookup } from './scene/index.js';
import {
  budgetLoadCount,
  readRenderBudget,
  snapshotRenderInfo,
  type RendererInfoLike,
} from './scene/renderProbe.js';
import { countSubmittable } from './scene/activeSlot.js';
import { fullDetailAssetsActive, packBaseAssetsActive } from './scene/assetDetail.js';
import { FlameSystem } from './scene/flame.js';
import { resolveFlameConfig } from './scene/flameConfig.js';
import { installEmitterFactory } from './assets/emitters.js';
import { FirstFrameGate, firstFrameGateDump } from './scene/firstFrameGate.js';
import { AssetUpgradeGate, UpgradeQueue, proxyFirstEnabled } from './scene/assetUpgradeGate.js';
import {
  PendingAssets,
  READINESS_SAMPLE_MS,
  anchorInView,
  drawRange,
  evaluateReadiness,
  objectAnchor,
  placeholderBodiesInView,
  viewVolume,
  type ContentPackState,
  type ReadinessSession,
} from './readiness.js';
import {
  FLAT_GROUND,
  buildGroundSurface,
  seatY,
  settlementLevelPads,
  type GroundSurface,
} from './scene/terrain.js';
import { easeTerrainPitch, terrainRenderPitch } from './terrainCamera.js';
import { healthBarLocalY } from './healthBarAnchor.js';
import {
  EquipmentRenderer,
  resolveGatherTool,
  bindSkinnedEquipment,
  morphSourceOf,
  gripArchetype,
  installSkinDevBridge,
  skinPreferenceForBuild,
  subscribeSkinPreference,
  type GatherToolBridge,
  type GatherMoment,
} from './equipment/index.js';
import type { EquipmentBridge } from './equipment/index.js';
import { CreatureRenderer } from './creatures/index.js';
import type { CreatureBridge } from './creatures/index.js';
import {
  PlayerRigRenderer,
  applyHeldGripMorphs,
  createThreeRigMixer,
  rigSocketPlacement,
  reverseGrip,
  type MorphRoot,
} from './rig-avatar/index.js';
import {
  DEFAULT_CRAFT_KIND,
  PLAYER_ATTACK_COOLDOWN_SECONDS,
  SwingGate,
  type SwingVerdict,
  abilityMotion,
  comboLength,
  gatherKindForSkill,
  weaponClassForEquipped,
  type RigAction,
  type RigActionMomentSource,
  type RigBridge,
  type WeaponClass,
} from './rig-avatar/index.js';
import { NpcPlacer, createNpcMarker } from './npcs/index.js';
import type { NpcBridge } from './npcs/index.js';
import { NodePlacer, createNodeHighlight, setNodeHighlightProgress } from './nodes/index.js';
import type { NodeBridge } from './nodes/index.js';
// O-025 techdemo dungeon layer (client-local; `?dungeons=1`) + playtest menu (`?devmenu=1`).
import { DUNGEON_CATALOG, DungeonWorldLayer, dungeonsEnabled } from './dungeon/layer.js';
import { DungeonRunClient } from './dungeon/runClient.js';
import { ServerEntranceShrines } from './dungeon/serverShrines.js';
import { shrineModelSource, type ShrineModelSource } from './dungeon/shrineRenderer.js';
import { ownBodyIn } from './dungeon/run.js';
import { DungeonRunScene } from './dungeon/runScene.js';
import {
  easeRunPitch,
  runBoomDistance,
  runLiftPitch,
  runOccluderTop,
} from './dungeon/runCamera.js';
import { setDevBridge } from './devBridge.js';
import { cameraMoveDone, cameraMovePose, type BoomPose, type CameraMove } from './cameraMove.js';
import { devToolsEnabled, effectiveSearch } from './devFlags.js';
import { PackLoaderCache } from './assets/index.js';
import { runTeardown } from './teardown.js';
import { buildTelegraphVisual } from './combat/telegraphVisual.js';
import {
  headingTo,
  pickSoftTarget,
  planAttack,
  SOFT_TARGET_THREAT_RANGE_M,
  stepApproach,
  telegraphCovers,
  ThreatMemory,
  type HostileView,
  type SoftTargetPick,
} from './combat/softTarget.js';
import { explicitTarget, type ExplicitTarget } from './combat/explicitTarget.js';
import { reconcileMode } from './dashBlend.js';
import { applyMirrorImageLook, isSummonedImage } from './mirrorImage.js';
import { gameFeedback } from './feedback/feedbackBus.js';
import { ImpactFeel, shakeAllowed } from './feedback/impactFeel.js';
import { VfxDirector, type WorldPoint } from './vfx/director.js';
import {
  VFX_GALLERY_NAMES,
  abilityFeel,
  galleryEvents,
  galleryFreezeMs,
  type VfxAnchor,
} from './vfx/cues.js';
import { holdActionFor } from './combat/holdAction.js';
import {
  PICK_BODY_HEIGHT_M,
  PICK_BODY_RADIUS_M,
  pickHostileAt,
  pickTolerancePx,
  type PointerKind,
  type ScreenHostile,
} from './combat/hostilePick.js';
import { attachTapControls, TapControls } from './tapControls.js';
import { easeYaw } from './yawEase.js';
import { audioDirector, audioEnterZone } from './audio/index.js';
import { noteRendererKind } from '../net/bugReport.js';

export interface GameStatus {
  phase: ConnectionPhase;
  phaseDetail: string;
  rttMs: number;
  serverTick: number;
  players: number;
  dodgeReadyPct: number;
  predictionErrorCm: number;
  /** Own combat health (P4.6), shown in the HUD. */
  health: number;
  healthMax: number;
  dead: boolean;
  flash: { text: string; kind: 'hit' | 'dodged' } | null;
  notice: string;
  /** Client content pack handshake (P4.9): announced hash + whether a pack is loaded. */
  contentPack: { hash: string; loaded: boolean } | null;
  /** Inside a dungeon run: the predicted body is in the instance frame, so the HUD offers no world prompts. */
  inDungeonRun: boolean;
  /** The snapshot rate this session is served at (D1; `helloAck.snapshotHz`, 15 when absent). */
  snapshotHz: number;
  /**
   * The follow camera's yaw (radians, the value WASD is mapped through) — the HUD's wayfinder
   * turns its north mark and objective arrow by it (q-player-names-and-wayfinding).
   */
  cameraYaw: number;
}

interface RemoteEntity {
  latest: EntityState;
  buffer: InterpolationBuffer;
  mesh: THREE.Group;
  /** Floating health bar (combat entities only — enemies + remote players). */
  healthBar?: THREE.Group;
  healthFill?: THREE.Mesh;
  /**
   * The bar's height over the entity's ground point (m), MEASURED from the drawn model's
   * bounds (q-enemy-healthbar-above-model) — at build, and again when the placeholder swaps
   * to the loaded proxy/rig. Read every frame, never re-measured per frame.
   */
  barY: number;
}

interface TelegraphVisual {
  group: THREE.Group;
  /** Drives the wind-up fill (telegraphVisual.ts) — uniform scale for most kinds, radial for a ring. */
  setProgress: (progress: number) => void;
  startTick: number;
  resolveTick: number;
  /** The wire shape (the DEV dump reports whether it covers us — AD-828's touch proof). */
  shape: TelegraphShape;
}

const FLASH_DURATION_MS = 1200;
const CORRECTION_SMOOTH_MS = 150;
const CORRECTION_SNAP_M = 1.0;
/**
 * AD-828: after the server reports OUR ability dash (`abilityUsed.dash`), the next reconcile's
 * jump (up to the dash cap) is blended through the correction offset — the body glides over
 * ~CORRECTION_SMOOTH_MS instead of teleporting. The window only bounds a stale flag.
 */
const DASH_BLEND_WINDOW_MS = 1000;

/**
 * The own rig's key in the player-rig renderer — client-local, never a wire id
 * (those are ≥ 0). Keyed by the world id it collided inside a run, whose zone
 * numbers bodies from its own space: a friend's instance id equal to my world id
 * drove, killed and finally removed MY rig. Wire ids reach it only through
 * `rigKeyFor`.
 */
export const OWN_RIG_KEY = -2;

/**
 * The skinned-armour binder for one rendered rig (q-player-armour-visual-fit, EQP-004): a
 * skinned piece is re-bound to THIS rig's bones (looked up under the rig root, never the
 * avatar group, so another piece's stray bone can never be found) and takes the body's
 * build-morph influences. A rigid piece returns false and keeps its socket placement.
 */
function skinnedEquipmentBinder(rigRoot: THREE.Object3D): (object: THREE.Object3D) => boolean {
  return (object) =>
    bindSkinnedEquipment(
      object,
      (name) => rigRoot.getObjectByName(name) ?? null,
      morphSourceOf(rigRoot),
    );
}

function entityColor(id: number, isOwn: boolean): THREE.Color {
  if (isOwn) return new THREE.Color('#4da6ff');
  const hue = ((id * 47) % 360) / 360;
  return new THREE.Color().setHSL(hue, 0.55, 0.6);
}

/**
 * Dev-only collision-footprint overlay, enabled with `?colliders=1` (WR.3.3).
 * Reads the EFFECTIVE search (O-026) so the playtest panel's persisted toggle
 * works as well as a typed URL; a URL param still wins.
 */
/** `?skystill=1` (dev flag): hold the sky's cloud drift at t = 0 — AD-9007. */
function skyStillEnabled(): boolean {
  try {
    return new URLSearchParams(effectiveSearch()).get('skystill') === '1';
  } catch {
    return false;
  }
}

function colliderOverlayEnabled(): boolean {
  try {
    return new URLSearchParams(effectiveSearch()).get('colliders') === '1';
  } catch {
    return false;
  }
}

/**
 * Is this a DEV build? Read defensively, exactly as `devFlags.devToolsEnabled()`
 * does and for the same reason: Vite replaces `import.meta.env` with a
 * compile-time object so a production build still statically drops the branch,
 * but under the dependency-free Node test runner `import.meta.env` is
 * `undefined` and the bare `import.meta.env.DEV` form threw a TypeError before
 * this module could finish loading — which is why nothing had ever constructed
 * a `Game` in a test.
 */
function devBuild(): boolean {
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
  return env?.DEV === true;
}

/** Render budget tier (WR.3.4): a `?tier=` override, else coarse-pointer/touch ⇒ mobile. */
function renderTier(): RenderTier {
  try {
    const forced = new URLSearchParams(effectiveSearch()).get('tier');
    if (forced === 'mobile' || forced === 'desktop') return forced;
    const coarse =
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    if (coarse || (navigator.maxTouchPoints ?? 0) > 1) return 'mobile';
  } catch {
    /* fall through to desktop */
  }
  return 'desktop';
}

export class Game {
  private readonly renderer: GameRenderer;
  private readonly rendererKind: RendererKind;
  /** The WR.3.4 budget tier resolved at construction; the render-budget probe compares against it. */
  private renderBudgetTier: RenderTier = 'desktop';
  /** DEV-only: the last frame's `renderer.info` counters, copied right after its render. */
  private lastFrameRenderInfo: RendererInfoLike | null = null;
  private scene = new THREE.Scene();
  /** Tier-based quality profile (VQ-001/AD-194), resolved ONCE at construction. */
  private readonly quality: QualityProfile;
  /** Owns tone mapping + the backend-specific post-fx chain; frame() delegates here. */
  private readonly postfx: PostFxLike;
  private orbit: OrbitCamera;
  private input = new InputState();
  private raycaster = new THREE.Raycaster();
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  private ownEntityId = -1;
  /**
   * The id the CURRENT snapshot/event stream gives this client's body (AD-705).
   * Equal to `ownEntityId` until a dungeon run moves the body to another zone,
   * which allocates its own ids (an instance id can even equal the world id of
   * someone else's body). The own rig is filed under `OWN_RIG_KEY`, never an id;
   * every comparison against wire ids uses this one (`rigKeyFor`).
   */
  private ownWireId = -1;
  /** The character's own name (unique, AD-001 index) — how the run's full snapshot names our body. */
  private ownName: string | null = null;
  /**
   * A run began or ended: FULL snapshots re-bind `ownWireId` (AD-705) until one names
   * our body under a NEW id — a stale keyframe from the zone we are leaving still
   * names the old one, and must not spend the latch.
   */
  private rebindOwnOnFull = false;
  /** One-shot latch so a failing animation frame warns once, not 60×/second. */
  private animWarned = false;
  /** The own swing's presentation gate (q-player-attack-no-restart): chain, never restart. */
  private readonly swingGate = new SwingGate(PLAYER_ATTACK_COOLDOWN_SECONDS, 1 / TICK_RATE);
  private bounds: ZoneBounds = { minX: -40, maxX: 40, minZ: -40, maxZ: 40 };
  /** Static collider from the join descriptor — the EXACT collider the server
   *  integrates with, so prediction can never disagree about an obstacle (AD-026). */
  private collider: WorldCollision | undefined;
  /** Descriptor-driven scene (WR.3.3) — replaces the hardcoded placeholder world. */
  private readonly sceneBuilder: SceneBuilder;
  /** The last join descriptor, re-applied when the content pack (theme/assetPack) loads. */
  private lastWorld: WorldDescriptor | null = null;
  /** The joined zone's id — the BIOME id (`zoneDefFromBiome` sets `zone.id = biome.id`),
   *  so it is the key into the content pack's biome `generation` dressing (AD-315). */
  private lastZoneId: string | null = null;
  /** AD-408: the joined zone's visual ground (flat unless its biome binds a terrain set).
   *  Render-only — movement, prediction and collision stay on the flat plane. */
  private ground: GroundSurface = FLAT_GROUND;
  /** AD-408: the join's authoritative spawn (`helloAck.you`) — the ground datum point. */
  private groundDatum = { x: 0, z: 0 };
  private predicted: MovementState = createMovementState(0, 0);
  private pending: Array<{ seq: number; input: InputPayload }> = [];
  private correction = { x: 0, z: 0 };
  private predictionErrorCm = 0;
  private ownMesh: THREE.Group | null = null;
  /** Self-view equipment renderer (E2.2.S2): attaches equipped items' base meshes
   *  to `ownMesh` and follows the inventory-snapshot store. Created when the avatar
   *  builds, torn down when it clears/disposes. */
  private equipmentRenderer: EquipmentRenderer | null = null;
  private equipmentUnsub: (() => void) | null = null;
  /** q-npc-props-adoption-and-flame: the runtime flames (null with `?flames=off`). */
  private flames: FlameSystem | null = null;
  /** Self-view gather-tool beat (E2.2.S3): the skill-matched tool drawn IN the mainhand during a
   *  LOCAL gather (`EquipmentRenderer.setHeldTool`), driven off the store's `lastGather` slice.
   *  Presentation-only, off the per-tick path (the tool carries no collision/stats). */
  private gatherToolUnsub: (() => void) | null = null;
  /** Identity of the last-seen gather moment — a NEW object ⇒ a fresh local gather. */
  private lastGatherSeen: GatherMoment | null = null;
  /** Handle for the auto-hide timer that ends the held-tool "harvest beat". */
  private gatherToolTimer: number | null = null;
  /** Enemy creature-mesh renderer (E2.3.S2): upgrades each enemy entity's placeholder
   *  cone+eye to its blockout proxy in-place, resolved client-side by name (the wire
   *  carries no enemy contentId). Created in the ctor when a creature bridge is present;
   *  its name→def map builds lazily once the content pack loads. Off the per-tick path. */
  private creatureRenderer: CreatureRenderer | null = null;
  /** Player rig avatar renderer (E2.4.S2): upgrades each player entity's placeholder
   *  capsule+nose to the skinned CHR-026 rig in-place, applying its `appearance`
   *  (AD-137) — self from `helloAck.you`, remotes from full state. Created in the ctor
   *  when a rig bridge is present; a missing/unloadable pack keeps the capsule
   *  (graceful). Off the per-tick path (appearance is full-state only). */
  private playerRigRenderer: PlayerRigRenderer | null = null;
  /** Current self rig root + mainhand model drive the event-only equipment grip morph. */
  private ownRigRoot: MorphRoot | null = null;
  /**
   * q-mobile-first-frame-defer-creatures-and-npc-clips: on the low tier, holds the enemy
   * rigs and the NPC clip pack until the own rig is on screen (released at once elsewhere).
   */
  private firstFrameGate: FirstFrameGate | null = null;
  /**
   * q-desktop-proxy-first-asset-upgrade: desktop full detail draws proxies until the loading
   * screen lifts, then swaps the textured bases in from `assetUpgrades`, nearest first.
   */
  private assetUpgradeGate: AssetUpgradeGate | null = null;
  private readonly assetUpgrades = new UpgradeQueue();
  /**
   * q-asset-readiness-loading-screen (AD-794): every loader reports its in-flight loads here,
   * anchored where they will appear; `sampleReadiness` feeds the in-view subset to the
   * loading screen's session (App.tsx) until it latches.
   */
  private readonly pendingAssets = new PendingAssets();
  private contentPackState: ContentPackState = 'pending';
  private lastReadinessSampleMs = -Infinity;
  private readinessWarned = false;
  private ownMainhandModel: string | null = null;
  /** The rendered main-hand mesh, which picks the hilt or haft fist (q-grip-fist-inner-hand-see-through). */
  private ownMainhandMesh: string | null = null;
  /** The local player's appearance (E2.4.S2), captured from `helloAck.you.appearance`
   *  at onReady and carried into the own avatar's rig build (the self path otherwise
   *  drops it). `DEFAULT_APPEARANCE` until the first join. */
  private ownAppearance: CharacterAppearance = DEFAULT_APPEARANCE;
  /** NPC in-world placer (E2.4.S3): stands each join-only `helloAck.npcs` NPC at its
   *  (x, z), resolved id→def-`presentation`→else the humanoid rig pack (default
   *  appearance)→else nothing. Created in the ctor when an NPC bridge is present;
   *  static join-only data, so it re-places off `onReady`/`onContentPack`. Off the
   *  per-tick path (the NPCs carry no collision/stats). */
  private npcPlacer: NpcPlacer | null = null;
  /** NPC ids the quest log is ready to turn in at (AD-867; onQuestLog). */
  private turnInNpcs: ReadonlySet<string> = new Set();
  /** NPCs with something for the player (offerNpcSet); null until a log names them. */
  private offerNpcs: ReadonlySet<string> | null = null;
  /** E3.4.S3: resource-node in-world placer (AD-141 node->asset seam). */
  private nodePlacer: NodePlacer | null = null;
  /** The last join ack's NPCs (E2.4.S3), re-placed when the content pack finishes
   *  loading (the rig/def packs resolve only once the pack is in) — like `lastWorld`. */
  private lastNpcs: NpcWire[] | null = null;
  /** The join ack's resource nodes, kept so a late content pack can re-place them. */
  private lastNodes: NodeWire[] | null = null;
  /** q-demo-vfx-pass: pooled game-feel effects, cued from the shared feedback bus. */
  private vfx: VfxDirector | null = null;
  /** q-combat-game-feel: hit stop + trauma shake, fed by `gameFeedback` (presentation only). */
  private readonly impact = new ImpactFeel();
  /** Last frame's predicted Dodge time left (a 0 → >0 edge is a Dodge start). */
  private vfxPrevDodgeMs = 0;
  /** Where the last body fell — its loot burst plays there after the body is gone. */
  private vfxLastDeath: { id: number; x: number; z: number } | null = null;
  /** DEV `?vfxgallery=<name>`: replay one effect on a loop and freeze it for the lane. */
  private vfxGallery: string | null = null;
  private vfxGalleryAtMs = 0;
  private vfxGalleryFreezeAtMs = 0;
  /** O-025 techdemo dungeon-entrance layer (client-local, `?dungeons=1` only; nothing
   *  on the wire — the pure package lifecycle runs against the LOCAL player position). */
  private dungeonLayer: DungeonWorldLayer | null = null;
  private dungeonPackLoaders: PackLoaderCache | null = null;
  /** E4.6.S3 slice 2d: the NETWORKED entry at server-advertised entrances (default-on). */
  private dungeonRunClient: DungeonRunClient | null = null;
  /** The shrine at each server-advertised entrance, for every client (no flag). */
  private dungeonEntrances: ServerEntranceShrines | null = null;
  /** E4.6.S3 slice 2e (AD-705): the run's interior at the instance frame; the world hidden meanwhile. */
  private dungeonRunScene: DungeonRunScene | null = null;
  /** AD-706: the drawn run's tallest wall top — the orbit boom is pulled in below it. */
  private runWallTop: number | null = null;
  /** AD-706: the eased render pitch while a run is drawn (null outside one). */
  private runPitch: number | null = null;
  /** q-follow-camera-vs-relief: the eased slope-relative pitch over relief (null on flat ground). */
  private terrainPitch: number | null = null;
  /** The open world's ground while a run is drawn (the run stands on flat floor at y 0). */
  private worldGround: GroundSurface | null = null;
  /** A world rebuild (late content pack) asked for mid-run — applied when the run ends. */
  private worldStaleForRun = false;

  private entities = new Map<number, RemoteEntity>();
  /** Hostiles whose telegraph covered / blow hit us lately — the soft target's first choice. */
  private readonly threats = new ThreatMemory();
  /** An attack press walking to its soft target (combat.md §Control model); null when idle. */
  private attackApproach: { targetId: number; startedMs: number; walking: boolean } | null = null;
  /** The hostile the player clicked/tapped (AD-817) — shared with the store's soft-target pick. */
  private readonly explicit: ExplicitTarget = explicitTarget;
  /** Click / tap / hold / drag / pinch on the canvas (AD-817; `tapControls.ts`). */
  private readonly tapControls = new TapControls({
    pickHostile: (x, y, kind) => this.pickHostileAtPx(x, y, kind),
    moveTo: (x, y) => this.groundMoveAtPx(x, y),
    attack: (id) => this.tapAttack(id),
    hold: (id) => this.tapHold(id),
    orbit: (dx, dy) => this.orbit.orbitBy(dx, dy),
    zoom: (ratio) => this.orbit.zoomBy(ratio),
  });
  private detachTapControls: () => void = () => {};
  /** Weapon abilities from the last `loadoutState` (slot 1 = the hold action's ability, AD-817). */
  private loadout: { weaponAbilities: readonly string[] } | null = null;
  private telegraphs = new Map<number, TelegraphVisual>();
  private moveMarker: THREE.Mesh;
  /** A ring under the explicit target (reuses the move marker's geometry kind; AD-817). */
  private targetMarker: THREE.Mesh;
  private readonly pickScratch = new THREE.Vector3();

  private tickOffset: number | null = null;
  private lastServerTick = 0;
  private inputAccumMs = 0;
  private lastFrameMs = 0;
  private rafHandle = 0;
  private running = false;
  /** q-no-device-lost-recovery-either-renderer: set once the renderer dies after
   *  start; the loop is halted and `onRendererLost` subscribers are told. */
  private lostRenderer: RendererLossInfo | null = null;
  private rendererLostListener: ((info: RendererLossInfo) => void) | null = null;
  /** Detaches the loss watch; run FIRST in dispose(), whose forceContextLoss()
   *  would otherwise raise the very event the watch reports. */
  private detachLossWatch: () => void = () => {};

  /** E1.5 capture presentation: in-scene health-bar visibility + slow-orbit drift.
   *  Client-LOCAL only (nothing rides the socket) — set by the UI CaptureController. */
  private healthBarsVisible = true;
  private slowOrbit = false;
  /**
   * Capture framing (captureFraming.ts, W42): the headings the capture camera may
   * face without the map edge in shot. Re-resolved (`framingDirty`) on a preset
   * apply, a slow-orbit toggle, a join ack and a respawn, once the own avatar stands
   * in the zone — and whenever it goes stale (`framingStale`: the avatar moved off
   * `framingAnchor`, or the pose shows void anyway); `null` outside capture mode
   * (normal play is never constrained).
   */
  private framing: OrbitFraming | null = null;
  private framingDir: 1 | -1 = 1;
  private framingDirty = true;
  /** Where the avatar stood when `framing` was resolved (the arc is only valid there). */
  private framingAnchor: { x: number; z: number } | null = null;
  /** The `__GC_DEVCAM` move in flight (dev builds only), eased from its start pose. */
  private cameraMove: { move: CameraMove; from: BoomPose; startMs: number } | null = null;

  private status: GameStatus = {
    phase: 'connecting',
    phaseDetail: '',
    rttMs: 0,
    serverTick: 0,
    players: 0,
    dodgeReadyPct: 1,
    predictionErrorCm: 0,
    cameraYaw: 0,
    health: 100,
    healthMax: 100,
    dead: false,
    flash: null,
    notice: '',
    contentPack: null,
    inDungeonRun: false,
    snapshotHz: TICK_RATE,
  };
  /**
   * How far in the past remote entities render (D1 `q-snapshot-rate-switch`): the longest
   * snapshot gap at this session's rate plus one tick of margin — INTERP_DELAY_MS (133 ms) at
   * 15 Hz, 200 ms at 10 Hz. Set from every ack; own-player prediction does not depend on it.
   */
  private interpDelayMs = INTERP_DELAY_MS;
  private flashUntilMs = 0;
  /** AD-828: until when a reconcile may blend an own ability dash (0 = no dash pending). */
  private dashBlendUntilMs = 0;
  private lastStatusPushMs = 0;

  /** DEV-only: the bound window.__gcSceneDump callback for THIS instance, tracked so
   *  dispose() only detaches its own hook — never a live sibling's (O-003). */
  private sceneDumpHook: ((part?: string) => Record<string, unknown>) | null = null;

  /**
   * WGP-001 follow-up: teardowns for the NON-renderer resources the constructor
   * acquires, in acquisition order (newest last).
   *
   * `App.tsx` already reclaims the *injected* renderer and its canvas when
   * `new Game()` throws (`disposeCreatedRenderer` — forceContextLoss + dispose +
   * `domElement.remove()`), but it can reach nothing else: a throw after the
   * first listener/global/GPU acquisition left window listeners, the dev
   * bridge, the `__gcSceneDump` hook, the composer targets and the three
   * renderer sub-objects alive with no instance to dispose them.
   *
   * Each entry is registered at its point of acquisition and run in REVERSE by
   * `rollbackConstruction()`, so the rollback list cannot drift away from the
   * acquisition list the way a hand-written mirror does. `dispose()` stays the
   * release path for a successfully constructed instance and is unchanged.
   */
  private ctorCleanup: Array<() => void> = [];
  /**
   * The ctor acquisitions `dispose()` has no step of its own for (the skin dev
   * bridge, the action-moment subscriptions, the budget fixture). Each is ALSO on
   * `ctorCleanup` so a failed construction unwinds it; `dispose()` runs this list
   * where it drops the ledger. It used to drop the ledger without running it, so
   * these outlived the instance (H-1349).
   */
  private lifetimeCleanup: Array<() => void> = [];

  /** Register a ctor acquisition released by BOTH the rollback and `dispose()`. */
  private ownForLifetime(teardown: () => void): void {
    this.ctorCleanup.push(teardown);
    this.lifetimeCleanup.push(teardown);
  }

  constructor(
    private readonly container: HTMLElement,
    private readonly conn: GameConnection,
    private readonly onStatus: (status: GameStatus) => void,
    createdRenderer: CreatedRenderer,
    private readonly content?: SceneContentLookup,
    /** Self-view equipment presentation (E2.2.S2). Absent ⇒ no equipment renders. */
    private readonly equipment?: EquipmentBridge,
    /** Self-view gather-tool held prop (E2.2.S3). Absent ⇒ no gather tool renders. */
    private readonly gatherTool?: GatherToolBridge,
    /** Enemy creature meshes (E2.3.S2). Absent ⇒ enemies stay cone+eye placeholders. */
    private readonly creature?: CreatureBridge,
    /** Player rig avatars (E2.4.S2). Absent ⇒ players stay capsule+nose placeholders. */
    private readonly rig?: RigBridge,
    /** NPC in-world placement (E2.4.S3). Absent ⇒ NPCs are not rendered (today's baseline). */
    private readonly npc?: NpcBridge,
    /** Resource-node in-world placement (E3.4.S3). Absent => nodes stay HUD-only. */
    private readonly node?: NodeBridge,
    /** The loading screen's readiness latch (AD-794). Absent ⇒ nothing is sampled. */
    private readonly readiness?: ReadinessSession,
  ) {
    this.renderer = createdRenderer.renderer;
    this.rendererKind = createdRenderer.kind;
    noteRendererKind(createdRenderer.kind);
    container.appendChild(this.renderer.domElement);
    // WGP-001 follow-up: everything below this line is transactional. The
    // renderer and the canvas above are the caller's to reclaim (App.tsx's
    // `disposeCreatedRenderer`); every resource acquired from here on registers
    // its own teardown and is unwound in reverse if construction fails.
    try {
      this.orbit = new OrbitCamera(1);
      // VQ-001 (AD-194): resolve the tier-based quality profile ONCE — desktop gets
      // the full pipeline (bloom + SSAO + AgX + full shadow maps); mobile or
      // `?quality=low` degrades (no SSAO/bloom, shadow maps halved, AgX retained).
      // Published on the readonly singleton for future per-setting overrides.
      const tier = renderTier();
      this.renderBudgetTier = tier;
      // O-026: the EFFECTIVE search — the URL overlaid with the playtest panel's
      // persisted overrides (URL wins). In a production build this is exactly
      // `window.location.search`, so deployed behaviour is unchanged.
      const search = effectiveSearch();
      this.quality = resolveQualityProfile(tier, search);
      setActiveQualityProfile(this.quality);
      this.firstFrameGate = new FirstFrameGate({ enabled: this.quality.low });
      this.assetUpgradeGate = new AssetUpgradeGate({
        enabled: proxyFirstEnabled({
          qualityLow: this.quality.low,
          capturePreference: capturedTexturesPreference(),
          search,
          hasLoadingScreen: this.readiness !== undefined,
        }),
      });
      // Both renderer paths share the same passPlan()/quality decision core. The
      // WebGL2 default retains AD-194's EffectComposer verbatim; the WebGPU branch
      // receives its dynamically loaded RenderPipeline/TSL factory from the async
      // renderer result, keeping WebGPU code out of the fallback bundle.
      this.postfx =
        createdRenderer.kind === 'webgpu'
          ? createdRenderer.createPostFx(
              createdRenderer.renderer,
              this.quality,
              this.scene,
              this.orbit.camera,
            )
          : new PostProcessingStack(
              createdRenderer.renderer,
              this.quality,
              this.scene,
              this.orbit.camera,
            );
      // Composer render targets are GPU memory: reclaim them before the caller
      // disposes the renderer itself.
      this.ctorCleanup.push(() => this.postfx.dispose());
      // q-npc-props-adoption-and-flame: the runtime flame. Installed BEFORE any asset loads,
      // so every template an AssetLoader caches gets its pack's declared flames (and loses
      // the authored static flame). `?flames=off` installs nothing: assets as authored.
      const flameConfig = resolveFlameConfig(this.quality, search);
      if (flameConfig.enabled) {
        const flames = new FlameSystem({
          config: flameConfig,
          scene: this.scene,
          createMaterial:
            createdRenderer.kind === 'webgpu' ? createdRenderer.createFlameMaterial : undefined,
        });
        this.flames = flames;
        const uninstall = installEmitterFactory((emitter) => flames.create(emitter));
        this.ownForLifetime(() => {
          uninstall();
          flames.dispose();
          if (this.flames === flames) this.flames = null;
        });
      }
      // WR.3.3: descriptor-driven scene. Built with the real glTF loader + delivery
      // origin; renders the exact legacy placeholder until a join descriptor (and
      // its content-pack-resolved theme/assetPack) arrives.
      this.sceneBuilder = new SceneBuilder(this.scene, {
        gltfLoad: createThreeGltfLoader({
          renderer: this.renderer,
        }),
        httpBase: assetHttpBase(),
        colliderOverlay: colliderOverlayEnabled(),
        tier,
        shadows: this.quality.shadows,
        // AD-315: biome dressing spans multiple asset packs, so the builder
        // resolves pack defs itself rather than through the single bound manifest.
        resolvePack: (packId) => this.content?.assetPack(packId),
        // 2026-09-16: a real tileable ground texture replaces the procedural detail noise that
        // read as blocky cells at distance (groundAlbedo.ts). Presentation only.
        loadGroundTexture: createBrowserGroundTextureLoad(),
        // AD-651 amendment: the ground material forks on the renderer exactly as post-FX
        // does — WebGPURenderer never calls onBeforeCompile, so the webgpu path gets the
        // TSL sibling and the WebGL2 default keeps its GLSL byte-for-byte.
        groundMaterial:
          createdRenderer.kind === 'webgpu' ? createdRenderer.createGroundMaterial : undefined,
        // AD-651 item 2: the sky dome forks the same way — three's TSL SkyMesh on webgpu,
        // the GLSL Sky.js default otherwise.
        sky: createdRenderer.kind === 'webgpu' ? createdRenderer.createSky : undefined,
        // AD-9007: `?skystill=1` holds the cloud drift still (determinism for the lane).
        skyStill: skyStillEnabled(),
        // E3.3.S5 (AD-159): the grass wind is GLSL onBeforeCompile, which WebGPURenderer never
        // calls — the webgpu path gets the TSL sibling (q-webgpu-grass-and-flame-parity).
        groundCover:
          createdRenderer.kind === 'webgpu' ? createdRenderer.createGroundCover : undefined,
        // AD-794: props, dressing, markers and the ground texture report their loads.
        pending: this.pendingAssets,
        upgradeGate: this.assetUpgradeGate,
        upgradeQueue: this.assetUpgrades,
      });
      this.sceneBuilder.buildPlaceholder();
      this.ctorCleanup.push(() => this.sceneBuilder.clear());
      // E3.3.S1 (AD-158 constraint 7): count a whole frame, not its last pass — with
      // auto-reset on, every composer pass restarts `renderer.info` and the probe would
      // read one full-screen quad. `frame()` resets once per frame instead.
      this.renderer.info.autoReset = false;
      if (devBuild()) this.addBudgetLoadFixture(budgetLoadCount(search));
      this.moveMarker = this.buildMoveMarker();
      this.targetMarker = this.buildTargetMarker();
      // q-demo-vfx-pass: game-feel effects (hit sparks, heal glow, Dodge trail, gather and
      // craft sparkles, milestone bursts, slam emphasis, death/respawn). Presentation only:
      // a failure here costs the effects, never the game.
      try {
        const vfx = new VfxDirector({
          scene: this.scene,
          tier,
          resolve: (anchor) => this.vfxPoint(anchor),
          bus: gameFeedback,
          overlayHost: container,
        });
        this.vfx = vfx;
        if (devBuild()) {
          const params = new URLSearchParams(search);
          const gallery = params.get('vfxgallery');
          this.vfxGallery =
            gallery === 'all' || (VFX_GALLERY_NAMES as readonly string[]).includes(gallery ?? '')
              ? gallery
              : null;
          if (params.get('vfxhold') === '1') vfx.setHold(0.85);
        }
        this.ownForLifetime(() => {
          vfx.dispose();
          if (this.vfx === vfx) this.vfx = null;
        });
      } catch (error) {
        console.warn('[vfx] effects unavailable; rendering continues', error);
      }
      // q-combat-game-feel: hit stop + screen shake hear the same bus (presentation only).
      const unsubImpact = gameFeedback.subscribe((event) =>
        this.impact.onFeedback(event, this.ownEntityId, performance.now()),
      );
      this.ownForLifetime(unsubImpact);

      this.orbit.attach(this.renderer.domElement);
      this.ctorCleanup.push(() => this.orbit.dispose());
      this.input.attach(this.renderer.domElement);
      this.ctorCleanup.push(() => this.input.dispose());
      this.detachTapControls = attachTapControls(
        this.renderer.domElement,
        this.tapControls,
        () => performance.now(),
        (x, y) => this.hoverAt(x, y),
      );
      this.ctorCleanup.push(() => this.detachTapControls());
      window.addEventListener('resize', this.onResize);
      this.ctorCleanup.push(() => window.removeEventListener('resize', this.onResize));
      this.onResize();
      // A renderer that dies AFTER construction: halt the loop and say so, rather
      // than drive a dead context behind a black canvas.
      this.detachLossWatch = watchRendererLoss(createdRenderer, (info) =>
        this.handleRendererLost(info),
      );
      this.ctorCleanup.push(() => this.detachLossWatch());

      // O-026: publish the narrow dev bridge so the React playtest panel can reach
      // this Game's client-local dungeon layer. Dev builds only (the panel itself
      // is dropped from production by the same gate), cleared on dispose. This
      // replaces the O-025 raw-DOM F9 overlay, which the panel supersedes.
      if (devToolsEnabled()) {
        setDevBridge({
          dungeonLayer: () => this.dungeonLayer,
          cameraMove: (move) => this.startCameraMove(move),
        });
        this.ctorCleanup.push(() => setDevBridge(null));
        // Weapon-skin playtest switch (`window.__GC_DEVSKIN`, weapon-skins.md):
        // same gate, same lifetime; the visual lane's only handle on skins.
        // The probe answers from the LIVE equipment renderer (built later, on the
        // avatar's first attach) so `set` refuses a skin that cannot apply to what
        // is equipped — a verdict the lane reads instead of a silent no-op.
        this.ownForLifetime(
          installSkinDevBridge(undefined, (slot, cosmeticId) =>
            this.equipmentRenderer !== null
              ? this.equipmentRenderer.skinApplies(slot, cosmeticId)
              : false,
          ),
        );
      }

      // Capture diagnostics (DEV builds only — `devBuild()` reads the Vite-inlined
      // env, so nothing ships in production): expose a scene-graph snapshot for
      // the studio's diagnose_capture tool (2026-W28 vanishing-world investigation).
      // Client-local, read-only, nothing on the wire (AD-031/AD-124 untouched).
      if (devBuild()) {
        // `__gcSceneDump('combat')` is the cheap read (combat block + death flag only): a lane
        // watching wind-up rings polls it at ~20 Hz, where the full dump walks the scene.
        // `__gcSceneDump('world')` is the zone's walkable world exactly as this client predicts
        // movement with it (bounds + the join ack's collider): a lane walk plans its way round a
        // fence from it instead of pressing into it (q-guest-gather-nodes-1-4-unreached-by-walk).
        this.sceneDumpHook = (part?: string) =>
          part === 'combat'
            ? { combat: this.combatDump(), ownDead: this.status.dead }
            : part === 'world'
              ? { world: { bounds: this.bounds, collision: this.collider ?? null } }
              : this.sceneDump();
        (window as unknown as Record<string, unknown>).__gcSceneDump = this.sceneDumpHook;
        // Detach on the same "only if it is still ours" terms dispose() uses — a
        // discarded instance must never answer for the live one (O-003).
        this.ctorCleanup.push(() => this.detachSceneDumpHook());
      }

      // E2.3.S2: enemy creature-mesh renderer. Built once here (needs the shared
      // initialized renderer for its glTF loader); its name→def reverse map builds lazily
      // when the content pack first has enemies. No creature bridge ⇒ enemies keep
      // their placeholder cone+eye (the committed tree renders byte-identically today).
      if (this.creature) {
        this.creatureRenderer = new CreatureRenderer({
          content: this.creature.content,
          gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
          httpBase: assetHttpBase(),
          // q-creature-rigging step 5: a proxy whose GLB carries clips animates; a
          // static proxy (every shipped one today) is never attached and renders as before.
          createMixer: createThreeRigMixer(),
          // The low tier's first frame draws placeholders; the rigs load after it.
          holdUpgrades: this.firstFrameGate?.released === false,
          // The proxy replaced the cone: re-seat the health bar over the model's real top.
          onUpgraded: (id, object) => this.measureHealthBar(id, object),
        });
        this.firstFrameGate?.onRelease(() => this.creatureRenderer?.releaseUpgrades());
        this.ctorCleanup.push(() => {
          this.creatureRenderer?.dispose();
          this.creatureRenderer = null;
        });
      }

      // E2.4.S2: player rig avatar renderer. Built once here (needs the shared
      // initialized renderer for its glTF loader); drives self + remote player rig upgrades.
      // No rig bridge ⇒ players keep their placeholder capsule+nose (graceful — the
      // never-blank-avatar rule). The interim player-rig-v1 pack (AD-181) is committed
      // + staged, so with a bridge the committed tree renders the interim rig live
      // (owner-directed, 2026-07-10); a missing/unloadable pack still falls back to the
      // capsule.
      if (this.rig) {
        this.playerRigRenderer = new PlayerRigRenderer({
          content: this.rig.content,
          gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
          httpBase: assetHttpBase(),
          // AP5.S1: the browser-side mixer factory — its presence is what turns
          // animation on (node/test consumers omit it and keep static rigs).
          createMixer: createThreeRigMixer(),
          // The low tier's first frame waits on the body alone; the attack combos follow it.
          deferClipPacks: this.firstFrameGate?.released === false,
        });
        this.firstFrameGate?.onRelease(() => this.playerRigRenderer?.releaseClipPacks());
        this.ctorCleanup.push(() => {
          this.playerRigRenderer?.dispose();
          this.playerRigRenderer = null;
        });
        // AP5.S2: a NEW successful local gather/craft plays the matching one-shot
        // on the own rig. The signals are the store's `lastGather`/`lastCraft`
        // slices (already delivered by the wire — nothing new is sent); combat
        // actions come off the event channel in onEvent instead.
        const moments = this.rig.moments;
        if (moments !== undefined) {
          const unsubscribes = [
            this.watchActionMoment(moments.gather, 'gather'),
            this.watchActionMoment(moments.craft, 'craft'),
          ];
          this.ownForLifetime(() => {
            for (const unsubscribe of unsubscribes) unsubscribe();
          });
        }
      }

      // E2.4.S3: NPC in-world placer. Built once here (shares the live renderer for its
      // glTF loader); places the join-only `helloAck.npcs` on `onReady` + re-places when
      // the content pack loads. No NPC bridge ⇒ NPCs are not rendered (today's baseline).
      if (this.npc) {
        const equipment = this.equipment;
        this.npcPlacer = new NpcPlacer({
          content: this.npc.content,
          scene: this.scene,
          gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
          httpBase: assetHttpBase(),
          // AP5.S2: the same mixer factory the player rigs get — placed NPCs idle
          // (de-phased per id) instead of standing in bind pose.
          createMixer: createThreeRigMixer(),
          groundHeightAt: (x, z) => this.ground.heightAt(x, z), // AD-408 seat
          // sprint-assembly-1 (AD-647): a floating "?" / "!" (q-quest-marker-glyph-semantics).
          createMarker: createNpcMarker,
          useFullDetailBase: fullDetailAssetsActive(), // a station's textured base
          upgrades: { gate: this.assetUpgradeGate, queue: this.assetUpgrades },
          // q-npc-props-adoption-and-flame: a set's hand props from npc-hand-props-v1 (a
          // smith's hammer, a torch whose runtime flame follows the hand) on the hand bones.
          attachHeldAssets: true,
          // Low tier: NPCs idle on the body's clips until the first frame is up.
          deferClipPacks: this.firstFrameGate?.released === false,
          pending: this.pendingAssets, // AD-794: the loading screen waits on NPCs in view
          // q-npc-props-existing-wiring: a set's held props (a smith's hammer) ride the
          // rig's hand bones through the same renderer a remote player's weapon uses.
          createHeldProps: equipment
            ? (root, held) => {
                const bones = rigSocketPlacement(
                  (name) => root.getObjectByName(name) ?? null,
                  root,
                );
                const reversed = new Set<string>(
                  held.filter((row) => row.reversed).map((row) => row.slot),
                );
                const renderer = new EquipmentRenderer({
                  target: root,
                  content: equipment.content,
                  pending: { sink: this.pendingAssets, anchor: objectAnchor(root) },
                  gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
                  httpBase: assetHttpBase(),
                  placement: (slot, model, mesh) => {
                    const placed = bones(slot, model, mesh);
                    if (placed?.transform === undefined || !reversed.has(slot)) return placed;
                    return { parent: placed.parent, transform: reverseGrip(placed.transform) };
                  },
                  onMainhandModelChange: (model, mesh) => applyHeldGripMorphs(root, model, mesh),
                });
                renderer.sync(
                  held.map(({ slot, item }) => ({
                    id: `npc-prop-${slot}`,
                    contentId: item,
                    slot,
                  })) as unknown as Parameters<EquipmentRenderer['sync']>[0],
                );
                return renderer;
              }
            : undefined,
        });
        this.firstFrameGate?.onRelease(() => this.npcPlacer?.releaseClipPacks());
        this.npcPlacer.setQuestMarkers(this.turnInNpcs, this.offerNpcs); // AD-867: a log that arrived first
        this.ctorCleanup.push(() => {
          this.npcPlacer?.dispose();
          this.npcPlacer = null;
        });
      }

      // E3.4.S3: resource-node in-world placer (the AD-141 node->asset seam). Same
      // shape as the NPC placer, with one deliberate difference: a node that cannot
      // resolve gets a PLACEHOLDER rather than nothing, because a node is
      // interactable and already on the HUD minimap — the E3.4.S2 lesson about a
      // thing the server treats as present and the client draws as absent.
      if (this.node) {
        this.nodePlacer = new NodePlacer({
          content: this.node.content,
          scene: this.scene,
          gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
          httpBase: assetHttpBase(),
          groundHeightAt: (x, z) => this.ground.heightAt(x, z), // AD-408 seat
          makePlaceholder: () => {
            const mesh = new THREE.Mesh(
              new THREE.CylinderGeometry(0.35, 0.45, 0.9, 8),
              new THREE.MeshStandardMaterial({ color: 0x6f7d5a, roughness: 0.9 }),
            );
            mesh.position.y = 0.45; // base at y=0, the AD-044 seating convention
            return mesh;
          },
          // sprint-assembly-1 (AD-647): a pale-gold ground ring marks every node as gatherable.
          makeHighlight: createNodeHighlight,
          setHighlightProgress: setNodeHighlightProgress, // AD-665 gather progress
          // Textured base, as creatures do; on low quality per the kit's `lowQualityBase`.
          useFullDetailBase: packBaseAssetsActive,
          pending: this.pendingAssets, // AD-794
        });
        this.ctorCleanup.push(() => {
          this.nodePlacer?.dispose();
          this.nodePlacer = null;
        });
      }
    } catch (error) {
      this.rollbackConstruction();
      throw error;
    }
  }

  /**
   * Unwind the constructor ledger, newest first. Every step is guarded on its
   * own: this runs on an ALREADY-failing construction, where a half-built
   * resource refusing to tear down is exactly the likely case, and one such
   * refusal must never strand the resources behind it. The original
   * construction error is the one that propagates — a teardown failure here is
   * strictly less informative than the fault that caused the rollback.
   */
  private rollbackConstruction(): void {
    for (const teardown of [...this.ctorCleanup].reverse()) {
      try {
        teardown();
      } catch {
        /* half-built resource refused teardown — keep unwinding the rest */
      }
    }
    this.ctorCleanup = [];
  }

  /**
   * Detach the DEV scene-dump hook, but ONLY if it still points at this instance:
   * a discarded instance must never answer `window.__gcSceneDump` for the live one
   * (O-003 — the capture read a healthy dump off one instance while a different,
   * world-less context was on screen). Shared by `dispose()` and the constructor
   * rollback so the two can never disagree about that condition.
   */
  private detachSceneDumpHook(): void {
    if (!this.sceneDumpHook) return;
    const w = window as unknown as Record<string, unknown>;
    if (w.__gcSceneDump === this.sceneDumpHook) w.__gcSceneDump = undefined;
    this.sceneDumpHook = null;
  }

  /**
   * DEV-only over-budget FIXTURE for the render-budget probe (`?budgetload=<n>`,
   * E3.3.S1): `n` individually-drawn one-triangle meshes, never culled, parked
   * far below the ground so they cost draw calls without changing the frame. It
   * exists so the lane can prove its budget check FAILS, not just that it passes.
   */
  private addBudgetLoadFixture(n: number): void {
    if (n <= 0) return;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0, 0, 1, 0], 3),
    );
    const material = new THREE.MeshBasicMaterial();
    const group = new THREE.Group();
    for (let i = 0; i < n; i += 1) {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.frustumCulled = false;
      mesh.position.set(0, -500, 0);
      group.add(mesh);
    }
    this.scene.add(group);
    this.ownForLifetime(() => {
      this.scene.remove(group);
      geometry.dispose();
      material.dispose();
    });
  }

  /** Dev-only diagnostic snapshot (window.__gcSceneDump) — see ctor note. */
  private sceneDump(): Record<string, unknown> {
    return {
      renderer: this.rendererKind,
      phase: this.status.phase,
      phaseDetail: this.status.phaseDetail,
      // The zone the server actually admitted this client to (join ack). 2026-09-16: the weekly
      // capture asked for the Bestiary and filmed the Nimbus plaza for weeks, because the server
      // only restores a checkpoint whose zone it HOSTS and otherwise drops the player at its own
      // default spawn — a silent substitution every readiness signal above still passes. The
      // studio compares this against the segment's requested zone and refuses the clip on a
      // mismatch, so a wrong-zone shot fails the render instead of shipping as footage.
      zoneId: this.lastZoneId,
      contentPack: this.status.contentPack,
      haveWorldDescriptor: this.lastWorld !== null,
      ownEntity: this.ownMesh !== null,
      predicted: { x: this.predicted.x, z: this.predicted.z },
      // q-client-soft-target-and-ability-hotkeys: who a press is aimed at, and the nearby
      // enemies' SERVER-reported health (snapshot `health`), so a lane run can show a hit landed.
      combat: this.combatDump(),
      // q-guest-demo-mobile-playable: what a lane needs to WALK BY TAP (tap the ground where a
      // world point projects) and to find a gather node, without a teleport or a move hook.
      nav: this.navDump(),
      // q-npc-ambient-animations: each animated NPC's `set:mode` (loop/beat/greet/talk).
      npcAmbient: this.npcPlacer?.ambientModes() ?? {},
      // q-npc-props-adoption-and-flame: flames drawn last frame + the light pool's use.
      flames: this.flames?.stats() ?? null,
      // q-mobile-first-frame-defer-creatures-and-npc-clips: when the low tier's deferred
      // loads (enemy rigs, NPC clip pack) were released (`performance.now()` ms; null =
      // never held, i.e. not the low tier), plus the asset bytes fetched before it.
      firstFrameGate: firstFrameGateDump(this.firstFrameGate),
      // q-desktop-proxy-first-asset-upgrade: held until the reveal, then nearest-first swaps.
      assetUpgrades: {
        // This session drew proxies first (still holding, or released at `releasedAtMs`).
        held:
          this.assetUpgradeGate?.released === false || this.assetUpgradeGate?.releasedAtMs != null,
        releasedAtMs: this.assetUpgradeGate?.releasedAtMs ?? null,
        ...this.assetUpgrades.state,
      },
      // AD-794: the loading screen's latch + the loads still in flight (anywhere, not just in view).
      readiness: this.readiness?.state ?? null,
      pendingAssets: {
        inFlight: this.pendingAssets.entries().length,
        started: this.pendingAssets.started,
        settled: this.pendingAssets.settled,
      },
      cameraPos: this.orbit.camera.position.toArray(),
      fog:
        this.scene.fog instanceof THREE.Fog
          ? {
              color: `#${this.scene.fog.color.getHexString()}`,
              near: this.scene.fog.near,
              far: this.scene.fog.far,
            }
          : null,
      background:
        this.scene.background instanceof THREE.Color
          ? `#${this.scene.background.getHexString()}`
          : String(this.scene.background),
      // Capture-readiness signals (owner-reported 2026-W35/W36 capsule placeholders on
      // screen at clip start; CloudMediaStudio `clip_capture.py` `_world_ready_js` reads
      // exactly these names and treats them as REQUIRED when present). `contentPack.loaded`
      // says the definitions arrived, not that the rig glTF/textures did — these say so.
      // W42 (2026-10-14): `ownRigged` / `playerRigs*` now also answer to the scene itself —
      // a capsule placeholder ATTACHED AND VISIBLE right now makes them read not-ready,
      // whatever the bookkeeping says (placeholderBodies.ts). `placeholderBodies` /
      // `capsuleFree` report the census directly.
      ...characterReadiness(
        this.ownRigRoot !== null,
        this.playerRigRenderer ? this.playerRigRenderer.stats() : null,
        countPlaceholderBodies(this.scene, this.ownMesh, this.orbit.camera),
      ),
      ownEquippedSlots: this.equipmentRenderer?.attachedCount() ?? 0,
      // W42 re-render: the northwatch delver was killed mid-hold by a wandering creature and
      // the music segment filmed the death pose and the respawn. The studio refuses a hold
      // during which this reads true (CloudMediaStudio clip_capture.py).
      ownDead: this.status.dead,
      // W42: the share of the frame showing void past the zone edge right now (0 = no map
      // edge in shot) and the orbit framing capture mode resolved (captureFraming.ts).
      // Measured against the REAL edge (margin 0): the studio refuses a hold the moment
      // this reads above 0 while `kind` is set (CloudMediaStudio clip_capture.py).
      captureFraming: {
        voidShare: this.ownMesh
          ? voidExposure(
              this.ownMesh.position,
              this.orbit,
              this.framingGround(),
              this.framingView(),
              0,
            )
          : null,
        kind: this.framing?.kind ?? null,
      },
      // 2026-W37: "the world descriptor arrived" is not "the zone is dressed". The
      // studio requires a floor on this count so a clip never opens on the
      // placeholder world (ground + grid + markers) while the kit is still streaming.
      worldMeshes: this.sceneBuilder.meshCount(),
      // E3.3.S2: static-merge progress; the S1 probe reads the frame after `pending` is 0.
      staticMerge: this.sceneBuilder.mergeStats(),
      markersHidden: !this.sceneBuilder.markersShown(),
      // E3.3.S1: what the live renderer submitted in the last frame vs RENDER_BUDGETS.
      vfx: this.vfx?.dump() ?? null,
      renderBudget: readRenderBudget(
        this.lastFrameRenderInfo ?? (this.renderer.info as unknown as RendererInfoLike),
        this.renderBudgetTier,
      ),
      // H-1145: the same frame split by pass (`shadow` out of the scene renders that drew it).
      passCalls: this.postfx.passCalls?.() ?? null,
      // E3.3.S4 (AD-158 §6): drawables the render camera can submit on each side of
      // the active slot. Inside a dungeon `exterior` must be 0 from ANY pose.
      activeSlot: {
        active: this.dungeonLayer?.slotRoot() != null,
        ...countSubmittable(this.scene, this.orbit.camera, this.dungeonLayer?.slotRoot() ?? null),
      },
      // E3.3.S5 (AD-159): instanced grass — chunks built, clumps, chunks inside the cull radius.
      groundCover: this.sceneBuilder.groundCoverStats(),
      sceneChildren: this.scene.children.map((c) => ({
        type: c.type,
        visible: c.visible,
        children: c.children.length,
        pos: [c.position.x, c.position.y, c.position.z],
        // Present only on short-lived combat feedback (telegraphs); the capture tooling excludes
        // these from its "the world has stopped changing" count.
        ...(typeof c.userData.transient === 'string' ? { transient: c.userData.transient } : {}),
      })),
    };
  }

  // ------------------------------------------------------------------
  // Descriptor-driven scene (WR.3.3) — see scene/SceneBuilder. The hardcoded
  // placeholder world that buildWorld()/buildWorldMarkers() rendered now lives
  // in the pure planScene() (the no-pack fallback) + the thin SceneBuilder; the
  // SceneBuilder also upgrades placeholders to delivered pack assets and gates
  // the collision-footprint overlay behind `?colliders=1`.
  // ------------------------------------------------------------------

  /** Resolve the current world's theme/assetPack from the content pack and (re)build the scene. */
  private applyWorldScene(): void {
    const world = this.lastWorld;
    if (!world) return;
    const pack = world.assetPack ? this.content?.assetPack(world.assetPack) : undefined;
    const theme = world.theme ? this.content?.theme(world.theme) : undefined;
    // AD-408: derive the ground from the biome's bound terrain set in the pack
    // (flat until the pack — and so the set — arrives; this re-applies on load).
    const biomeDef = this.lastZoneId ? this.content?.biome?.(this.lastZoneId) : undefined;
    const terrainId = clientTerrainIdOf(biomeDef);
    const terrainSet = terrainId ? clientTerrainSetOf(this.content?.terrainSet?.(terrainId)) : null;
    // q-terrain-level-pads: settlements stand on levelled ground, relief stays outside them.
    this.ground = buildGroundSurface(
      terrainSet,
      this.bounds,
      this.groundDatum,
      settlementLevelPads(clientGenerationOf(biomeDef), world.collision.shapes),
    );
    this.sceneBuilder.applyWorld(world, new AssetManifest(pack ?? null), theme ?? null, {
      // AD-315: the ground follows the ZONE'S bounds (always available from the
      // ack), while the dressing rules come from the content pack's biome
      // projection — null before the pack loads, so this re-applies on load
      // exactly like theme/assetPack do.
      bounds: this.bounds,
      biomeId: this.lastZoneId ?? '',
      generation: clientGenerationOf(biomeDef),
      // E3.3.S6: the zone tier weights its presentational population.
      tier: clientBiomeTierOf(biomeDef),
      packAssets: (packId) =>
        (this.content?.assetPack(packId)?.assets ?? []).map((asset) => asset.id),
      ground: this.ground,
    });
    this.seatDungeonEntrances();
    // Capture footage hides the landmark markers by default (E1.5 `pxl.*` flag,
    // 2026-W37 owner report); re-read per apply so a toggle mid-session applies
    // on the next world build without a reload.
    this.sceneBuilder.setMarkersVisible(!captureMarkersHidden());
  }

  /**
   * AD-408 seat for the entrance shrines — the server-advertised ones and the
   * flag-gated (`?dungeons=1`) techdemo ones: each child keeps its authored base y
   * in `userData` and is re-seated on every world apply, so a late pack (terrain
   * arrives) or a zone change never double-lifts.
   */
  private seatDungeonEntrances(): void {
    for (const child of [
      ...(this.dungeonEntrances?.group.children ?? []),
      ...(this.dungeonLayer?.group.children ?? []),
    ]) {
      const base = (child.userData.groundBaseY ??= child.position.y) as number;
      child.position.y = seatY(this.ground, child.position.x, child.position.z, base);
    }
  }

  private buildMoveMarker(): THREE.Mesh {
    const marker = new THREE.Mesh(
      new THREE.RingGeometry(0.25, 0.4, 24),
      new THREE.MeshBasicMaterial({ color: '#4da6ff', transparent: true, opacity: 0.9 }),
    );
    marker.rotation.x = -Math.PI / 2;
    marker.position.y = 0.02;
    marker.visible = false;
    this.scene.add(marker);
    return marker;
  }

  private buildTargetMarker(): THREE.Mesh {
    const marker = new THREE.Mesh(
      new THREE.RingGeometry(0.7, 0.9, 32),
      new THREE.MeshBasicMaterial({ color: '#ff5a4d', transparent: true, opacity: 0.85 }),
    );
    marker.rotation.x = -Math.PI / 2;
    marker.visible = false;
    this.scene.add(marker);
    return marker;
  }

  private buildEntityMesh(state: EntityState, own = false): THREE.Group {
    const group = new THREE.Group();
    const isOwn = own;
    if (state.kind === 'dummy') {
      const body = new THREE.Mesh(
        new THREE.CylinderGeometry(0.55, 0.7, 1.8, 10),
        new THREE.MeshStandardMaterial({ color: '#8a3b46', emissive: '#3d0f16', roughness: 0.7 }),
      );
      body.position.y = 0.9;
      body.castShadow = true; // VQ-001: entities ground themselves with sun shadows
      group.add(body);
      // VQ-001: eyes are emissive PBR (was unlit MeshBasic) so the bloom pass
      // reads them as glow points at gameplay distance.
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.14, 10, 10),
        new THREE.MeshStandardMaterial({
          color: '#1a0f05',
          emissive: '#ffb14d',
          emissiveIntensity: 2.4,
          roughness: 0.35,
        }),
      );
      eye.position.set(0, 1.45, 0.5);
      group.add(eye);
    } else if (state.kind === 'enemy') {
      const body = new THREE.Mesh(
        new THREE.ConeGeometry(0.6, 1.5, 6),
        new THREE.MeshStandardMaterial({ color: '#6f9a3b', emissive: '#20300f', roughness: 0.85 }),
      );
      body.position.y = 0.75;
      body.castShadow = true; // VQ-001
      // W42: tagged so the capture census (placeholderBodies.ts) can see a cone on screen.
      group.add(markPlaceholderBody(body, 'enemy'));
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.13, 8, 8),
        new THREE.MeshStandardMaterial({
          color: '#200607',
          emissive: '#ff5d6c',
          emissiveIntensity: 2.4,
          roughness: 0.35,
        }),
      );
      eye.position.set(0, 1.15, 0.5);
      group.add(eye);
    } else {
      const color = entityColor(state.id, isOwn);
      const body = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.4, 0.9, 4, 12),
        new THREE.MeshStandardMaterial({ color, roughness: 0.6 }),
      );
      body.position.y = 0.85;
      body.castShadow = true; // VQ-001
      // W42: the fallback "blue pill" — tagged so the capture census can refuse it on screen.
      group.add(markPlaceholderBody(body, 'player'));
      // Nose cone shows facing.
      const nose = new THREE.Mesh(
        new THREE.ConeGeometry(0.16, 0.34, 8),
        new THREE.MeshStandardMaterial({ color: color.clone().offsetHSL(0, 0, 0.15) }),
      );
      nose.rotation.x = Math.PI / 2;
      nose.position.set(0, 1.05, 0.48);
      nose.castShadow = true; // VQ-001
      group.add(nose);
    }
    group.position.set(state.x, seatY(this.ground, state.x, state.z, 0), state.z);
    group.rotation.y = state.facing;
    this.scene.add(group);
    return group;
  }

  /**
   * A small billboarded health bar (P4.6). Tracked separately from the body so it
   * faces the camera each frame; positioned above the entity in the frame loop.
   * The fill is anchored to its left edge so scaling x drains it from the right.
   */
  private buildHealthBar(): { group: THREE.Group; fill: THREE.Mesh } {
    const group = new THREE.Group();
    const width = 1.1;
    // The dark backing is a frame LARGER than the fill (q-enemy-healthbar-above-model): an
    // edge that reads against a dark model; at the fill's own size it framed nothing.
    const bg = new THREE.Mesh(
      new THREE.PlaneGeometry(width + 0.1, 0.16 + 0.08),
      new THREE.MeshBasicMaterial({ color: '#05070b', transparent: true, opacity: 0.9 }),
    );
    group.add(bg);
    const fill = new THREE.Mesh(
      new THREE.PlaneGeometry(width, 0.16),
      new THREE.MeshBasicMaterial({ color: '#5fd16b' }),
    );
    fill.geometry.translate(width / 2, 0, 0);
    fill.position.set(-width / 2, 0, 0.001);
    group.add(fill);
    group.visible = false;
    this.scene.add(group);
    return { group, fill };
  }

  // ------------------------------------------------------------------
  // Connection callbacks (wired by App)
  // ------------------------------------------------------------------

  /** The approved shrine GLBs (dungeon-entrance-kit-v1, H-1322); null before the pack loaders exist. */
  private shrineModels(): ShrineModelSource | null {
    const cache = this.dungeonPackLoaders;
    if (!cache) return null;
    return shrineModelSource((packId) => {
      const def = this.content?.assetPack(packId);
      return def ? cache.loaderFor(def) : null;
    });
  }

  /** Content pack handshake result (P4.9): the announced hash and whether a pack is loaded. */
  onContentPack(hash: string, loaded: boolean): void {
    this.status.contentPack = hash ? { hash, loaded } : null;
    // AD-794: a pack that failed to load is terminal — the loading screen stops waiting on it.
    this.contentPackState = loaded ? 'loaded' : 'unavailable';
    // The pack carries the theme + asset-pack defs; re-resolve so a just-loaded
    // pack upgrades the placeholder world to its real props (WR.3.3).
    // AD-705: mid-run, the world is hidden and `bounds`/`ground` are the run's —
    // rebuilding it now would build it on the instance's frame. Deferred to the exit.
    // Keyed on the RUN, not the drawing: the prediction takes the run's bounds the moment
    // the server says `entered`, while `show(plan)` is async (and an unmatched or
    // unlisted run is never drawn at all).
    const inRun = this.dungeonRunClient?.run != null || this.dungeonRunScene?.active === true;
    if (loaded && inRun) this.worldStaleForRun = true;
    const rebuildWorld = loaded && !inRun;
    // q-own-rig-load-priority: the player's own rig is requested BEFORE the world rebuild,
    // whose ~90 dressing/scatter/NPC loads otherwise queue ahead of the body on the wire and
    // in the decoders. Measured on a throttled phone: the body requested last took 38 s to
    // parse and the player moved as a frozen figure meanwhile. Settled/in-flight rigs no-op.
    if (loaded) this.attachPlayerRig();
    if (rebuildWorld && this.lastWorld) this.applyWorldScene();
    // E2.4.S3: the just-loaded pack carries the rig + any NPC-def presentation packs,
    // so (re)place the NPCs now that they can resolve (they no-op'd at onReady if the
    // pack was not yet loaded).
    if (rebuildWorld && this.lastNpcs) this.npcPlacer?.place(this.lastNpcs);
    // E3.4.S3: likewise the node kit — nodes placed before the pack arrived are
    // standing on placeholders, so re-placing swaps in their archetype meshes.
    if (rebuildWorld && this.lastNodes) this.nodePlacer?.place(this.lastNodes);
    // E2.4.S6 (gate ROUND-1 F1): the same pack carries the rig def, so re-attempt the
    // rig upgrade for every player still on its capsule fallback — on a non-loopback
    // deploy the first render precedes pack arrival, and a null build DELETED its
    // renderer record, so exactly the capsule players retry (settled/in-flight rigs
    // no-op via the record guard). Self re-seats equipment via attachPlayerRig's
    // onBuilt callback; remotes carry no equipment (self-view only).
    if (loaded) {
      // H-1322: the pack declares dungeon-entrance-kit-v1, so the server shrines built at
      // the helloAck (before it arrived) now swap their primitives for the approved GLBs.
      const shrineModels = this.shrineModels();
      if (shrineModels) this.dungeonEntrances?.requestModels(shrineModels);
      for (const [id, entity] of this.entities) {
        if (entity.latest.kind === 'player') {
          this.upgradeRemotePlayer(id, entity.mesh, entity.latest.appearance ?? DEFAULT_APPEARANCE);
        }
      }
    }
  }

  /**
   * E4.6.S3 slice 2e (AD-705): draw the run at the instance frame. The world is
   * hidden and the ground goes flat (the interior floor is y 0), so every body the
   * run's snapshots place is seated on the floor it is simulated on.
   */
  private beginDungeonRunDraw(plan: DungeonPlan): void {
    if (!this.dungeonRunScene) return;
    // `??=`: a second draw before the first went active must not save FLAT_GROUND as
    // the world's (the exit would then restore a flat terrain zone).
    this.worldGround ??= this.ground;
    this.ground = FLAT_GROUND;
    this.runWallTop = runOccluderTop(plan);
    void this.dungeonRunScene.show(plan);
  }

  /** The run is over: interior gone, the world's ground back, a deferred rebuild applied. */
  private endDungeonRunDraw(reapply: boolean): void {
    // A run that never went active (undrawn, or still loading) still deferred the world.
    this.dungeonRunScene?.release(); // a no-op when nothing is drawn; cancels a pending show
    this.runWallTop = null;
    if (this.worldGround) this.ground = this.worldGround;
    this.worldGround = null;
    if (this.worldStaleForRun) {
      this.worldStaleForRun = false;
      if (reapply) {
        if (this.lastWorld) this.applyWorldScene();
        if (this.lastNpcs) this.npcPlacer?.place(this.lastNpcs);
        if (this.lastNodes) this.nodePlacer?.place(this.lastNodes);
      }
    }
  }

  /** E4.6.S3 slice 2d: the private `dungeonResult` (reply or run-ended push); the store toasts it. */
  onDungeonResult(d: DungeonResultPayload): void {
    // AD-705: the body is about to change zones; the next full snapshot names it.
    if (d.ok) this.rebindOwnOnFull = true;
    this.dungeonRunClient?.onResult(d);
  }

  onReady(ack: HelloAckPayload, resumed: boolean): void {
    // AD-705: an ack is world context — stop drawing any run BEFORE the world is
    // (re)applied, so the restore cannot resurrect roots this ack replaces. A
    // character still seated is told its run again right after (wsServer).
    this.endDungeonRunDraw(false);
    this.ownEntityId = ack.entityId;
    this.ownWireId = ack.entityId; // the ack names the body the character actually has
    // D1: the snapshot rate sizes the interpolation delay (absent = an older server = 15 Hz).
    this.status.snapshotHz = ack.snapshotHz ?? TICK_RATE;
    this.interpDelayMs = interpDelayMsFor(this.status.snapshotHz);
    this.ownName = ack.you.name;
    this.rebindOwnOnFull = false;
    this.bounds = ack.bounds;
    // E2.4.S2: retain the self appearance (AD-137) — the self path otherwise drops it
    // (only movement fields were read). Carried into the own avatar's rig build.
    this.ownAppearance = ack.you.appearance ?? DEFAULT_APPEARANCE;
    // Predict with the exact collider the server uses (WR.2). No shapes ⇒ undefined,
    // so the flat-plane prediction path is unchanged (AD-031).
    // AD-840: an island zone's coastline rides the collider, so outline-only still predicts.
    this.collider =
      ack.world.collision.shapes.length > 0 || ack.world.collision.outline !== undefined
        ? ack.world.collision
        : undefined;
    this.lastWorld = ack.world;
    this.lastZoneId = ack.zoneId;
    audioEnterZone(ack.zoneId); // AD-861: preload the zone's cues, music back to the yard
    this.groundDatum = { x: ack.you.x, z: ack.you.z }; // AD-408: datum = the arrival point
    this.applyWorldScene();
    // E2.4.S3: place the zone's join-only NPCs (static — never on the per-tick path).
    // They resolve only once the content pack is in (the rig + any def-presentation
    // packs come from it), so this is re-placed in onContentPack, exactly like
    // applyWorldScene above; place() clears any prior NPCs first (zone change).
    this.lastNpcs = ack.npcs;
    this.npcPlacer?.place(ack.npcs);
    this.lastNodes = ack.nodes;
    this.nodePlacer?.place(ack.nodes);
    // E4.6.S3 slice 2d: the networked entry at the server-advertised entrances.
    // Default-on since `q-dungeon-flag-lift-and-capture-set` (it was `?dungeons=1`, so an
    // ordinary client never offered the site and the devlog could not film it); a zone
    // that advertises no dungeon leaves it inert. Built BEFORE the techdemo layer so its
    // G listener runs first (a server entrance outranks a preview shrine). An ack is
    // world context, so any run this client thought it was in is over.
    this.dungeonRunClient ??= new DungeonRunClient(DUNGEON_CATALOG, {
      send: (t, d) => {
        if (t === 'dungeonEnter') this.conn.send('dungeonEnter', d as { dungeonId: string });
        else this.conn.send('dungeonLeave', {});
      },
      setPrediction: (bounds, collider) => {
        this.bounds = bounds;
        this.collider = collider;
      },
      warn: (message) => console.warn(`[dungeon] ${message}`),
      drawRun: (plan) => {
        if (plan) this.beginDungeonRunDraw(plan);
        else this.endDungeonRunDraw(true);
      },
    });
    this.dungeonRunClient.onWorld(ack.world.dungeons, ack.bounds, this.collider);
    this.dungeonPackLoaders ??= new PackLoaderCache({
      gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
      httpBase: assetHttpBase(),
    });
    const cache = this.dungeonPackLoaders;
    this.dungeonEntrances?.dispose();
    this.dungeonEntrances = new ServerEntranceShrines(
      this.scene,
      ack.world.dungeons ?? [],
      ack.zoneId,
      (message) => console.warn(`[dungeon] ${message}`),
      this.shrineModels(),
    );
    this.dungeonLayer?.dispose();
    this.dungeonLayer = null;
    this.dungeonRunScene ??= new DungeonRunScene(
      this.scene,
      this.orbit.camera,
      (packId) => {
        const def = this.content?.assetPack(packId);
        return def ? cache.loaderFor(def) : null;
      },
      DUNGEON_CATALOG,
      (root, camera, scene) => this.renderer.compileAsync(root, camera, scene),
      (message) => console.warn(`[dungeon] ${message}`),
    );
    // O-025: the client-local techdemo shrines + interior preview stay behind `?dungeons=1`.
    if (dungeonsEnabled(effectiveSearch())) {
      this.dungeonLayer = new DungeonWorldLayer(
        this.scene,
        this.orbit.camera,
        (packId) => {
          const def = this.content?.assetPack(packId);
          return def ? cache.loaderFor(def) : null;
        },
        ack.zoneId,
        String(ack.characterId),
        // Programs compile off the frame on entry (dungeon design §12d).
        (root, camera, scene) => this.renderer.compileAsync(root, camera, scene),
        ack.world.dungeons ?? [],
      );
    }
    this.seatDungeonEntrances();
    this.predicted = {
      ...createMovementState(ack.you.x, ack.you.z, ack.you.facing),
      dodgeRemainingMs: ack.you.dodgeRemainingMs,
      dodgeCooldownMs: ack.you.dodgeCooldownMs,
    };
    this.pending = [];
    this.correction = { x: 0, z: 0 };
    this.tickOffset = null;
    this.lastServerTick = ack.serverTick;
    if (!resumed) {
      this.clearRemoteEntities();
      if (this.ownMesh) {
        this.teardownEquipment(); // detach + dispose equipment before the avatar is dropped
        this.playerRigRenderer?.remove(OWN_RIG_KEY); // E2.4.S2: drop the own rig too
        this.discard(this.ownMesh);
        this.ownMesh = null;
        // W42: the rig went with the avatar. Left set, `ownRigged` read true while the
        // rebuilt avatar was drawn as the capsule — and a capture shipped it.
        this.ownRigRoot = null;
      }
    }
    // A join ack can move the avatar (zone change, checkpoint restore): re-frame the capture.
    this.framingDirty = true;
  }

  onPhase(phase: ConnectionPhase, detail?: string): void {
    this.status.phase = phase;
    this.status.phaseDetail = detail ?? '';
    this.pushStatus(true);
  }

  onRtt(rttMs: number): void {
    this.status.rttMs = rttMs;
  }

  onNotice(message: string): void {
    this.status.notice = message;
    this.pushStatus(true);
  }

  onEvent(tick: number, _seq: number, event: GameEvent): void {
    // AD-861: the swing, enemy telegraph cues and the music state (presentation only).
    audioDirector.noteGameEvent(event, this.ownWireId, (id) => this.entities.get(id)?.latest.name);
    switch (event.type) {
      case 'telegraph': {
        // The WHOLE shape: kind + facing/arc/length/innerRadius. The server resolves hits on that
        // geometry (pointInZone); drawing only x/z/radius showed a circle for a cone (VFX-018 D-2).
        this.spawnTelegraph(event.sourceId, event.shape, event.startTick, event.resolveTick);
        gameFeedback.emit({
          kind: 'telegraphStart',
          sourceId: event.sourceId,
          x: event.shape.x,
          z: event.shape.z,
          radius: event.shape.radius,
          heavy: event.heavy,
        });
        // Soft target: a telegraph that covers us marks its source as the one to answer.
        this.threats.noteTelegraph(
          event.sourceId,
          event.shape,
          { x: this.predicted.x, z: this.predicted.z },
          performance.now(),
        );
        // AD-636: a rigged enemy whose rig ships this attack's `Attack_<motion>` clip winds
        // up WITH the ring — the clip's contact is authored at the served wind-up.
        this.guardCreature(() =>
          this.creatureRenderer?.beginAttack(event.sourceId, event.attackId, event.heavy),
        );
        return;
      }
      case 'telegraphResolve': {
        const visual = this.telegraphs.get(event.sourceId);
        if (visual) {
          gameFeedback.emit({
            kind: 'telegraphLand',
            sourceId: event.sourceId,
            x: visual.shape.x,
            z: visual.shape.z,
            radius: visual.shape.radius,
            hitSelf: event.hits.includes(this.ownWireId),
          });
          this.discard(visual.group);
          this.telegraphs.delete(event.sourceId);
        }
        // A rigged enemy strikes as its telegraph lands — unless its motion clip is already
        // there (AD-636); a static proxy is a no-op.
        this.guardCreature(() => this.creatureRenderer?.landAttack(event.sourceId));
        // AP5.S2: everyone the telegraph landed on flinches (rigged players only —
        // an enemy id has no rig and is a no-op). The dodged keep their dodge state.
        for (const id of event.hits) this.presentAction(this.rigKeyFor(id), 'hit');
        this.threats.noteResolve(event.sourceId, event.hits, this.ownWireId, performance.now());
        if (event.hits.includes(this.ownWireId)) {
          // q-demo-vfx-pass: damage-taken cue from the EVENT channel (never the snapshot, AD-031).
          gameFeedback.emit({ kind: 'playerDamaged', amount: 1 });
          this.status.flash = { text: 'HIT', kind: 'hit' };
          this.flashUntilMs = performance.now() + FLASH_DURATION_MS;
        } else if (event.dodged.includes(this.ownWireId)) {
          this.status.flash = { text: 'DODGED', kind: 'dodged' };
          this.flashUntilMs = performance.now() + FLASH_DURATION_MS;
        }
        this.pushStatus(true);
        return;
      }
      case 'nodeDepleted':
      case 'nodeRespawned': {
        // Owner 2026-10-24: a depleted node loses its ground ring; a respawn brings it back.
        const depleted = event.type === 'nodeDepleted';
        this.nodePlacer?.setDepleted(event.nodeId, depleted);
        const known = this.lastNodes?.find((n) => n.id === event.nodeId);
        if (known) known.depleted = depleted; // a later re-place() keeps the live state
        return;
      }
      case 'playerJoined':
      case 'playerLeft':
        this.status.notice =
          event.type === 'playerJoined' ? `${event.name} joined` : `${event.name} left`;
        this.pushStatus(true);
        return;
      case 'attack': {
        // AP5.S2: the attacker's rig swings. Self was already predicted at input
        // time (frame()), so its echo is skipped — the dedupe; an enemy sourceId
        // has no rig and is a no-op inside the renderer.
        // AD-671: a remote swings with ITS weapon class (from its `held` items) and walks
        // that class's combo steps when its hits come at the cadence.
        if (event.sourceId !== this.ownWireId) this.presentRemoteAttack(event.sourceId);
        this.threats.noteAttack(event.sourceId, event.targetId, this.ownWireId, performance.now());
        // The struck enemy flinches; a killing blow's `entityDied` follows and wins.
        if (event.targetId !== null) {
          this.presentCreatureAction(event.targetId, 'hit');
          gameFeedback.emit({
            kind: 'weaponHit',
            sourceId: event.sourceId,
            targetId: event.targetId,
            heavy: false,
          });
          if (event.targetId === this.ownWireId)
            gameFeedback.emit({ kind: 'playerDamaged', amount: 1 });
        }
        return;
      }
      case 'abilityUsed': {
        if (event.targetId !== undefined) this.presentCreatureAction(event.targetId, 'hit');
        if (event.dash !== undefined && event.sourceId === this.ownWireId) {
          this.dashBlendUntilMs = performance.now() + DASH_BLEND_WINDOW_MS;
        }
        this.presentAbility(event.sourceId, event.abilityId);
        {
          const feel = abilityFeel(event.abilityId);
          gameFeedback.emit({
            kind: 'ability',
            sourceId: event.sourceId,
            abilityId: event.abilityId,
            feel,
          });
          if (event.targetId !== undefined && feel === 'strike')
            gameFeedback.emit({
              kind: 'weaponHit',
              sourceId: event.sourceId,
              targetId: event.targetId,
              heavy: true,
            });
          if (event.targetId === this.ownWireId && feel === 'strike')
            gameFeedback.emit({ kind: 'playerDamaged', amount: 1 });
        }
        return;
      }
      case 'entityDied': {
        // AP5.S2: the death clip clamps on its last frame until `playerRespawned`.
        this.presentAction(this.rigKeyFor(event.entityId), 'dead');
        {
          const at = this.vfxPoint({ at: 'entity', id: event.entityId });
          if (at !== null) this.vfxLastDeath = { id: event.entityId, x: at.x, z: at.z };
          gameFeedback.emit({
            kind: 'died',
            entityId: event.entityId,
            self: event.entityId === this.ownWireId,
          });
        }
        if (event.entityId === this.ownWireId) {
          this.status.flash = { text: 'DOWN', kind: 'hit' };
          this.flashUntilMs = performance.now() + FLASH_DURATION_MS;
          this.status.dead = true;
        }
        this.pushStatus(true);
        return;
      }
      case 'playerRespawned': {
        this.presentAction(this.rigKeyFor(event.entityId), null); // AP5.S2: release the held death pose
        gameFeedback.emit({
          kind: 'respawned',
          entityId: event.entityId,
          x: event.x,
          z: event.z,
          self: event.entityId === this.ownWireId,
        });
        if (event.entityId === this.ownWireId) {
          this.swingGate.reset(); // the server's attack cadence restarts with the new life
          this.arrivalSwingDueMs = null;
          this.status.dead = false;
          this.status.notice = 'respawned at the shrine';
          // W42: the respawn moved the avatar — the capture framing was resolved elsewhere.
          this.framingDirty = true;
        }
        this.pushStatus(true);
        return;
      }
    }
  }

  /**
   * Take a Game-built visual out of the scene AND free what it owns — telegraphs,
   * placeholder bodies, health bars. `disposeObject3D` leaves any template-shared
   * buffer under it (a proxy or rig clone) to its cache (H-1349). These used to be
   * `scene.remove`d only, leaking a geometry + material set per spawn.
   */
  private discard(object: THREE.Object3D): void {
    this.scene.remove(object);
    disposeObject3D(object);
  }

  private spawnTelegraph(
    sourceId: number,
    shape: TelegraphShape,
    startTick: number,
    resolveTick: number,
  ): void {
    const old = this.telegraphs.get(sourceId);
    if (old) this.discard(old.group);
    const { x, z } = shape;
    // Rim + fill for the shape's true geometry — circle/point exactly as shipped, cone as a
    // sector, line as a rectangle, ring as an annulus with a safe hole, cross as four arms —
    // in the Bible §14.1 reserved hostile hue (combat/telegraphVisual.ts, VFX-018 D-1/D-2).
    const built = buildTelegraphVisual(shape);
    if (built.geometry.degraded) {
      // VFX-018 §3: never throw, never silently mis-draw — the fallback over-warns and says so.
      console.warn(`[telegraph] source ${sourceId}: ${built.geometry.degraded.reason}`);
    }
    const group = built.group;
    group.position.set(x, seatY(this.ground, x, z, 0.03), z);
    // AD-408: lie the ring on the local slope (render-only) rather than letting a
    // flat disc bury half its edge in a hillside — the dodge read depends on it.
    if (this.ground.heightfield !== null) {
      const dx = this.ground.heightAt(x + 0.5, z) - this.ground.heightAt(x - 0.5, z);
      const dz = this.ground.heightAt(x, z + 0.5) - this.ground.heightAt(x, z - 0.5);
      const normal = new THREE.Vector3(-dx, 1, -dz).normalize();
      group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
    }
    // Tagged TRANSIENT for the capture tooling (2026-09-16). A telegraph lives for one wind-up and is
    // removed on resolve, so it is combat feedback, not world content. In the Bestiary 81 exhibits
    // demonstrate on staggered cadences, the scene's child count changes on every poll, and a
    // readiness check that waits for "the scene has stopped changing" could never be satisfied.
    group.userData.transient = 'telegraph';
    this.scene.add(group);
    this.telegraphs.set(sourceId, {
      group,
      setProgress: built.setProgress,
      startTick,
      resolveTick,
      shape,
    });
  }

  onSnapshot(tick: number, snapshot: SnapshotPayload): void {
    this.lastServerTick = tick;
    this.status.serverTick = tick;
    // Smoothed estimate of "what server tick is it right now".
    const instOffset = tick - performance.now() / TICK_MS;
    this.tickOffset =
      this.tickOffset === null
        ? instOffset
        : this.tickOffset + 0.1 * (instOffset - this.tickOffset);

    if (snapshot.full && this.rebindOwnOnFull) this.rebindOwnBody(snapshot);
    const seenIds = new Set<number>();
    for (const delta of snapshot.entities) {
      seenIds.add(delta.id);
      if (delta.id === this.ownWireId) {
        this.applyOwnDelta(delta, snapshot.lastProcessedSeq);
      } else {
        this.applyRemoteDelta(tick, delta);
      }
    }
    if (snapshot.full) {
      for (const [id, entity] of this.entities) {
        if (!seenIds.has(id)) {
          this.retireEntityVisual(id, entity); // E2.3.S2/E2.4.S2 drops; a dying rig lingers
          this.entities.delete(id);
        }
      }
    }
    for (const id of snapshot.removed) {
      const entity = this.entities.get(id);
      if (entity) {
        this.retireEntityVisual(id, entity); // E2.3.S2/E2.4.S2 drops; a dying rig lingers
        this.entities.delete(id);
      }
    }
    this.status.players =
      [...this.entities.values()].filter((e) => e.latest.kind === 'player').length + 1;
  }

  /**
   * AD-705: entering or leaving a dungeon run moves the body to a zone with its
   * own id space and sends no ack — only a full snapshot. Find our body there by
   * the character's unique name (kind `player`) and follow it: the stream's id
   * becomes `ownWireId`, unacked inputs (aimed at the old body) are dropped, and
   * the reconcile below snaps the prediction to where the server put us.
   */
  private rebindOwnBody(snapshot: SnapshotPayload): void {
    const mine = ownBodyIn(snapshot.entities, this.ownName);
    if (mine === null) return; // not this frame's: keep waiting for the one that carries us
    // Across a zone swap the ids are another space's: an old-zone entity sharing an id
    // with a new one would take its deltas as a partial update (an enemy's cone and
    // name walking as the party healer). This full snapshot is the whole visible set,
    // so everything is rebuilt from it — harmless if it is a stale old-zone keyframe.
    this.clearRemoteEntities();
    // The SAME id is a keyframe from the zone we are leaving (or a swap that numbered us
    // alike): keep the latch for the transfer's own full snapshot.
    if (mine === this.ownWireId) return;
    this.rebindOwnOnFull = false;
    this.ownWireId = mine;
    this.pending = [];
    this.correction = { x: 0, z: 0 };
  }

  /** Drop every remote entity and its visuals (a fresh ack, or a zone swap's id space). */
  private clearRemoteEntities(): void {
    this.impact.reset(); // q-combat-game-feel: a held id belongs to the old id space
    for (const [id, entity] of this.entities) {
      this.creatureRenderer?.remove(id); // E2.3.S2: drop any proxy / in-flight load
      this.playerRigRenderer?.remove(id); // E2.4.S2: drop any rig / in-flight build
      this.remoteSwings.delete(id); // AD-671
      this.dropRemoteEquipment(id);
      // Last: the renderers above have detached what they own from the body (H-1349).
      this.discard(entity.mesh);
      if (entity.healthBar) this.discard(entity.healthBar);
    }
    this.entities.clear();
    // The soft target's memory and any walk-to are in the old id space too.
    this.threats.clear();
    this.explicit.clear(); // AD-817: the explicit target is an old-space id too
    this.endAttackApproach();
    // Telegraphs are keyed by the old space's source ids too.
    for (const visual of this.telegraphs.values()) this.discard(visual.group);
    this.telegraphs.clear();
  }

  private ownAuthoritative: MovementState = createMovementState(0, 0);

  private applyOwnDelta(delta: EntityDelta, lastProcessedSeq: number): void {
    const a = this.ownAuthoritative;
    if (delta.x !== undefined) a.x = delta.x;
    if (delta.z !== undefined) a.z = delta.z;
    if (delta.vx !== undefined) a.vx = delta.vx;
    if (delta.vz !== undefined) a.vz = delta.vz;
    if (delta.facing !== undefined) a.facing = delta.facing;
    if (delta.dodgeRemainingMs !== undefined) a.dodgeRemainingMs = delta.dodgeRemainingMs;
    if (delta.dodgeCooldownMs !== undefined) a.dodgeCooldownMs = delta.dodgeCooldownMs;
    // Own combat health (P4.6) — surfaced in the HUD; not part of the movement
    // replay (combat is server-authoritative, never predicted).
    if (delta.health !== undefined) this.status.health = delta.health;
    if (delta.healthMax !== undefined) this.status.healthMax = delta.healthMax;
    if (this.status.health > 0) this.status.dead = false;
    // AD-833: a body that is ALREADY down when we (re)join — a resumed guest held at the death
    // card — reads as dead from its zero health; no `entityDied` is replayed on a resume.
    else if (delta.health === 0) this.status.dead = true;

    // Reconcile: rewind to the authoritative state and replay unacked inputs
    // (dash direction is client-remembered; the server derived the same value
    // from the same input, so replay stays deterministic).
    //
    // **The prune below is CONDITIONAL, and this is the only site that performs
    // it.** `lastProcessedSeq` rides the snapshot envelope unconditionally, but
    // this method runs only when the snapshot carries THIS client's own entity
    // delta (see the caller's `delta.id === this.ownWireId` branch), and the
    // server's `diffEntity` returns null for an entity it did not move. So a
    // client that stops sending — after a server-side `resyncAfterStall` orphaned
    // its queued inputs, say — keeps those entries in `pending` with no bound in
    // time, until its own entity next moves and a delta arrives. The buffer is
    // inert while it waits (nothing is retransmitted, nothing is replayed against
    // the server), so the cost is memory held, not divergence. Stated here
    // because the contract is written in `shared/src/protocol/messages.ts` and in
    // `docs/architecture/networking.md`, and a reader of the client alone would
    // otherwise read this filter as an unconditional prune. (AD-287 gate,
    // round 2 F-D; `q-c4-gate-onclose-p2p3`.)
    this.pending = this.pending.filter((p) => p.seq > lastProcessedSeq);
    let replayed: MovementState = {
      ...a,
      dodgeDirX: this.predicted.dodgeDirX,
      dodgeDirZ: this.predicted.dodgeDirZ,
    };
    for (const p of this.pending) {
      replayed = stepMovement(replayed, p.input, TICK_MS, this.bounds, this.collider);
    }
    const errX = this.predicted.x - replayed.x;
    const errZ = this.predicted.z - replayed.z;
    const err = Math.hypot(errX, errZ);
    this.predictionErrorCm = Math.max(this.predictionErrorCm * 0.9, err * 100);
    this.status.predictionErrorCm = Math.round(this.predictionErrorCm * 10) / 10;
    // An own ability dash (AD-828) moved the body server-side: blend that jump, once.
    const mode = reconcileMode(
      err,
      performance.now(),
      this.dashBlendUntilMs,
      CORRECTION_SNAP_M,
      MOBILITY_DASH_MAX_M,
    );
    if (mode === 'dash') this.dashBlendUntilMs = 0;
    if (err > 0.001) {
      if (mode !== 'snap') {
        this.correction.x += errX;
        this.correction.z += errZ;
      } else {
        this.correction.x = 0;
        this.correction.z = 0;
      }
    }
    this.predicted = replayed;
  }

  private applyRemoteDelta(tick: number, delta: EntityDelta): void {
    let entity = this.entities.get(delta.id);
    if (!entity) {
      // Entering visibility: the delta carries full state.
      const full = delta as EntityState;
      if (full.kind === undefined || full.name === undefined) return; // partial delta for unknown entity
      entity = {
        latest: { ...full },
        buffer: new InterpolationBuffer(),
        mesh: this.buildEntityMesh(full),
        barY: 0,
      };
      entity.barY = healthBarLocalY(entity.mesh);
      if (full.health !== undefined && full.healthMax !== undefined) {
        const bar = this.buildHealthBar();
        entity.healthBar = bar.group;
        entity.healthFill = bar.fill;
      }
      this.entities.set(delta.id, entity);
      // E2.3.S2: an enemy entering visibility asynchronously upgrades its placeholder
      // cone+eye to its blockout proxy (resolved by name; the group stays the anchor,
      // the cone stays the graceful fallback). Off the per-tick path (AD-156 bodyless).
      if (full.kind === 'enemy') {
        this.creatureRenderer?.upgrade(delta.id, entity.mesh, full.name);
      } else if (full.kind === 'player') {
        // E2.4.S2: a remote player entering visibility upgrades its placeholder
        // capsule+nose to the skinned rig, applying its full-state `appearance`
        // (AD-137). The group stays the anchor; the capsule stays the graceful
        // fallback. Remotes carry no equipment (self-view only) ⇒ no re-seat callback.
        this.upgradeRemotePlayer(delta.id, entity.mesh, full.appearance ?? DEFAULT_APPEARANCE);
      }
    } else {
      const latest = entity.latest;
      if (delta.x !== undefined) latest.x = delta.x;
      if (delta.z !== undefined) latest.z = delta.z;
      if (delta.vx !== undefined) latest.vx = delta.vx;
      if (delta.vz !== undefined) latest.vz = delta.vz;
      if (delta.facing !== undefined) latest.facing = delta.facing;
      if (delta.anim !== undefined) latest.anim = delta.anim;
      if (delta.health !== undefined) latest.health = delta.health;
      if (delta.healthMax !== undefined) latest.healthMax = delta.healthMax;
      // AD-671: an equip swap (null = both hands now empty).
      if (delta.held !== undefined) {
        if (delta.held === null) delete latest.held;
        else latest.held = delta.held;
        this.syncRemoteEquipment(delta.id);
      }
    }
    entity.buffer.push({
      tick,
      x: entity.latest.x,
      z: entity.latest.z,
      facing: entity.latest.facing,
      anim: entity.latest.anim,
    });
  }

  // ------------------------------------------------------------------
  // Pointer → click-to-move, click/tap a hostile, hold, touch camera (AD-817)
  // ------------------------------------------------------------------

  /** Canvas px → NDC for the raycaster / projections. */
  private canvasRect(): { left: number; top: number; width: number; height: number } {
    return this.renderer.domElement.getBoundingClientRect();
  }

  /** A ground click/tap at canvas px: click-to-move (unchanged rule), and it drops the explicit target. */
  private groundMoveAtPx(px: number, py: number): void {
    const rect = this.canvasRect();
    const ndc = new THREE.Vector2((px / rect.width) * 2 - 1, -(py / rect.height) * 2 + 1);
    this.orbit.camera.updateMatrixWorld(); // a press between frames sees the current boom
    this.raycaster.setFromCamera(ndc, this.orbit.camera);
    const hit = new THREE.Vector3();
    // AD-408: on relief the click lands on the drawn heightfield (the move target
    // is still a flat (x, z) — only WHERE the ray meets the ground changed).
    // AD-705: inside a run the floor is the y-0 plane, not the (hidden) world terrain.
    const terrain = this.dungeonRunScene?.active ? null : this.sceneBuilder.terrainObject();
    const terrainHit = terrain ? this.raycaster.intersectObject(terrain, false)[0] : undefined;
    if (terrainHit) hit.copy(terrainHit.point);
    if (terrainHit || this.raycaster.ray.intersectPlane(this.groundPlane, hit)) {
      // AD-840: the target is clamped exactly as the walk is — the bounds, then the coast.
      const { x, z } = clampToZone(this.bounds, this.collider, hit.x, hit.z);
      this.explicit.clear(); // a ground click drops the explicit target (combat.md §Control model)
      this.attackApproach = null; // a click-to-move overrides an attack's walk-to
      this.input.setMoveTarget(x, z);
      this.moveMarker.position.set(x, seatY(this.ground, x, z, 0.02), z);
      this.moveMarker.visible = true;
    }
  }

  /**
   * The hostile under canvas px (x, y): every live enemy inside the server's ability
   * bound (`SOFT_TARGET_THREAT_RANGE_M`, 26 m) is projected to a feet→head segment and
   * `pickHostileAt` takes the nearest within the pointer's tolerance (AD-817).
   */
  private pickHostileAtPx(px: number, py: number, kind: PointerKind): number | null {
    const rect = this.canvasRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const cam = this.orbit.camera;
    cam.updateMatrixWorld();
    const focalPx = rect.height / 2 / Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
    const v = this.pickScratch;
    const toPx = (): { x: number; y: number } | null =>
      v.z < -1 || v.z > 1
        ? null
        : { x: ((v.x + 1) / 2) * rect.width, y: ((1 - v.y) / 2) * rect.height };
    const candidates: ScreenHostile[] = [];
    for (const entity of this.entities.values()) {
      const s = entity.latest;
      if (s.kind !== 'enemy' || (s.health !== undefined && s.health <= 0)) continue;
      if (Math.hypot(s.x - this.predicted.x, s.z - this.predicted.z) > SOFT_TARGET_THREAT_RANGE_M)
        continue;
      const at = entity.mesh.position;
      v.set(at.x, at.y, at.z).project(cam);
      const foot = toPx();
      v.set(at.x, at.y + PICK_BODY_HEIGHT_M, at.z).project(cam);
      const head = toPx();
      if (foot === null || head === null) continue; // behind the camera
      const depth = Math.max(0.1, cam.position.distanceTo(at));
      candidates.push({
        id: s.id,
        footX: foot.x,
        footY: foot.y,
        headX: head.x,
        headY: head.y,
        radiusPx: (PICK_BODY_RADIUS_M * focalPx) / depth,
        depth,
      });
    }
    return pickHostileAt(px, py, candidates, pickTolerancePx(kind));
  }

  /** Mouse hover: a crosshair over a pickable hostile, the default cursor elsewhere. */
  private hoverAt(px: number, py: number): void {
    const style = this.renderer.domElement.style;
    const cursor = this.pickHostileAtPx(px, py, 'mouse') !== null ? 'crosshair' : '';
    if (style.cursor !== cursor) style.cursor = cursor;
  }

  /** A click/tap on a hostile: explicit target + one basic-attack press (walk-to if out of reach). */
  private tapAttack(id: number): void {
    this.explicit.set(id);
    this.input.queueAttack();
  }

  /**
   * A held press on a hostile: explicit target + the hold action (`holdActionFor`, the
   * q-charged-attacks seam). Today: weapon ability 1 at the target, faced. The pick is
   * already inside the server's 26 m ability bound, so the cast never needs a walk; the
   * server re-checks the bound and its refusal is the truthful answer.
   */
  private tapHold(id: number): void {
    this.explicit.set(id);
    const action = holdActionFor(this.loadout);
    if (action.kind === 'basicAttack') {
      this.input.queueAttack();
      return;
    }
    const target = this.hostileViews().find((h) => h.id === id);
    if (target !== undefined)
      this.input.setFacing(headingTo({ x: this.predicted.x, z: this.predicted.z }, target));
    this.conn.send('abilityIntent', { abilityId: action.abilityId, targetEntityId: id });
  }

  /**
   * The quest log (store-routed from App, AD-867): a quest ready to hand in puts a "!" over its
   * turn-in NPC, and an NPC with something for the player wears a "?"
   * (q-quest-marker-glyph-semantics). Generic per quest state; the HUD and map read the same log.
   */
  onQuestLog(log: {
    quests: readonly TurnInQuestLike[];
    offerNpcs?: readonly string[];
    infoNpcs?: readonly string[];
  }): void {
    this.turnInNpcs = readyTurnInNpcs(log.quests);
    this.offerNpcs = offerNpcSet(log);
    this.npcPlacer?.setQuestMarkers(this.turnInNpcs, this.offerNpcs);
  }

  /** The server's loadout (store-routed from App): weapon ability 1 is the hold action. */
  onLoadout(loadout: { weaponAbilities: readonly string[] }): void {
    this.loadout = { weaponAbilities: [...loadout.weaponAbilities] };
  }

  /** Per frame: drop a dead/despawned explicit target and seat its ring under it. */
  private updateExplicitTarget(): void {
    const id = this.explicit.id;
    if (id !== null && !this.explicit.validate(this.hostileViews())) {
      this.targetMarker.visible = false;
      return;
    }
    const entity = id === null ? undefined : this.entities.get(id);
    if (entity === undefined) {
      this.targetMarker.visible = false;
      return;
    }
    const at = entity.mesh.position;
    this.targetMarker.position.set(at.x, seatY(this.ground, at.x, at.z, 0.03), at.z);
    this.targetMarker.visible = true;
  }

  private onResize = (): void => {
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h);
    this.postfx.setSize(w, h); // keep the composer targets in step (VQ-001)
    this.orbit.setAspect(w / h);
  };

  // ------------------------------------------------------------------
  // Frame loop
  // ------------------------------------------------------------------

  // ---- E1.5 capture-mode presentation hooks (client-local; nothing on the wire) ----

  /** Show/hide the in-scene health-bar sprites (the capture beauty-shot sub-flag). */
  setCaptureHealthBars(visible: boolean): void {
    this.healthBarsVisible = visible;
  }

  /** Enable/disable the slow-orbit yaw drift (presentation-only). */
  setSlowOrbit(on: boolean): void {
    this.slowOrbit = on;
    this.framingDirty = true;
  }

  /**
   * Start a timed camera move (`__GC_DEVCAM`, q-capture-camera-channel-devcam-bridge).
   * The move owns the boom while it runs — slow-orbit drift and a preset snap are
   * overridden until it ends — and a new move replaces one in flight, easing from
   * wherever the camera is now. The op was validated against CAMERA_BOUNDS by
   * `parseCameraMove`; `applyPose` clamps again, so neither layer alone holds it.
   */
  startCameraMove(move: CameraMove): boolean {
    const { yaw, pitch, dist } = this.orbit;
    this.cameraMove = { move, from: { yaw, pitch, dist }, startMs: performance.now() };
    return true;
  }

  /** Snap the orbit camera to a named preset pose (bounds enforced in OrbitCamera). */
  applyCameraPreset(id: string): void {
    this.orbit.applyPose(presetById(id));
    this.framingDirty = true;
  }

  /**
   * The ground capture framing measures void against: the zone rectangle, except on an AD-840
   * island zone, where the camera sees the cloud sea past it (scene builder `visibleGroundExtent`).
   */
  private framingGround(): FramingRect {
    return this.sceneBuilder.visibleGroundExtent() ?? this.bounds;
  }

  /** The camera projection capture framing judges a pose with (fog bounds what ground shows). */
  private framingView(): FramingView {
    const cam = this.orbit.camera;
    const fog = this.scene.fog instanceof THREE.Fog ? this.scene.fog : null;
    const fogFar = Math.min(fog?.far ?? cam.far, cam.far);
    return {
      fovYDeg: cam.fov,
      aspect: cam.aspect,
      fogNear: Math.min(fog?.near ?? fogFar, fogFar),
      fogFar,
    };
  }

  /**
   * Capture framing (captureFraming.ts): in capture mode, turn the camera to a
   * heading with no map edge in shot and bound the slow orbit to the safe arc.
   * Outside capture mode, or while a dungeon run owns the frame, nothing is
   * constrained. Yaw only — pitch/dist (the CAMERA_BOUNDS envelope) are untouched.
   */
  private resolveCaptureFraming(target: THREE.Vector3): void {
    this.framingDirty = false;
    if (!readFlag(CAPTURE_KEYS.captureMode) || this.dungeonRunScene?.active) {
      this.framing = null;
      return;
    }
    const framing = resolveOrbitFraming(
      target,
      this.orbit,
      this.framingGround(),
      this.framingView(),
    );
    this.framing = framing;
    this.framingAnchor = { x: target.x, z: target.z };
    // A re-frame that keeps the current heading keeps the sweep's direction too (no
    // visible stop-and-reverse); a turn to a new arc starts it fresh.
    if (framing.startYaw !== this.orbit.yaw) this.framingDir = 1;
    this.orbit.yaw = framing.startYaw;
    if (framing.kind === 'hold') {
      console.warn(
        `[capture] every heading shows the map edge here (best still ${(framing.exposure * 100).toFixed(1)}% void) — holding the least-bad one`,
      );
    }
  }

  // ------------------------------------------------------------------
  // Self-view equipment presentation (E2.2.S2)
  // ------------------------------------------------------------------

  /**
   * Create the equipment renderer against the freshly-built `ownMesh` and wire it
   * to the inventory-snapshot store: attach for already-equipped items now, and
   * re-sync on every store change (equip/unequip/replace). No-op without an
   * equipment bridge, without an avatar, or if already attached. Its own glTF
   * loader shares the WebGLRenderer (KTX2) and the same-origin asset base.
   */
  private attachEquipment(): void {
    if (!this.equipment || !this.ownMesh || this.equipmentRenderer) return;
    // Capture mode films the rig-v2 loadout by default after the equipment re-seat.
    // The sub-flag remains for deliberate gear-free body/animation diagnostics.
    // Read once at attach time — a capture sets the flag before login, and the
    // renderer is rebuilt with the avatar on every join/rig swap.
    if (captureEquipmentHidden()) return;
    const renderer = new EquipmentRenderer({
      target: this.ownMesh,
      content: this.equipment.content,
      pending: { sink: this.pendingAssets, anchor: objectAnchor(this.ownMesh) }, // AD-794
      gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
      httpBase: assetHttpBase(),
      onMainhandModelChange: (model, mesh) => {
        this.ownMainhandModel = model;
        this.ownMainhandMesh = mesh;
        if (this.ownRigRoot !== null) {
          applyHeldGripMorphs(this.ownRigRoot, model, mesh);
        }
      },
      // Client-local weapon-skin preference (weapon-skins.md): consulted per sync;
      // a change re-runs the resolve chain so the affected slot re-attaches. Dev
      // builds only until server ownership lands (`skinPreferenceForBuild`).
      skinPreference: skinPreferenceForBuild(devToolsEnabled()),
    });
    const source = this.equipment.equipped;
    const resync = (): void => renderer.sync(source.get());
    const unsubItems = source.subscribe(resync);
    const unsubSkins = subscribeSkinPreference(() => renderer.refresh());
    this.equipmentUnsub = () => {
      unsubItems();
      unsubSkins();
    };
    this.equipmentRenderer = renderer;
    resync(); // initial attach for items equipped before the avatar existed
  }

  /** Unsubscribe + dispose the equipment renderer (detaches + frees its meshes),
   *  and the gather beat (E2.2.S3), together with the avatar. */
  private teardownEquipment(): void {
    if (this.equipmentUnsub) {
      this.equipmentUnsub();
      this.equipmentUnsub = null;
    }
    if (this.equipmentRenderer) {
      this.equipmentRenderer.dispose();
      this.equipmentRenderer = null;
    }
    this.ownMainhandModel = null;
    this.ownMainhandMesh = null;
    if (this.ownRigRoot !== null) applyHeldGripMorphs(this.ownRigRoot, null);
    this.teardownGatherTool();
  }

  /**
   * Wire the gather beat to the store's gather slice (E2.2.S3): on a NEW successful local gather
   * the skill-matched tool is drawn in the mainhand for a short "harvest beat", then the hand goes
   * back to the equipped item. The tool goes through the equipment renderer
   * (`setHeldTool`), so it is seated on the hand bone with its held grip and closes the fist, and
   * the hand draws ONE thing: the tool replaces the weapon for the beat, and a mainhand that already
   * holds the tool (the server's auto-swap, AD-844) is left as it is. q-gather-tool-one-in-hand: the
   * beat was a second prop on the avatar group at the capsule-era grip, so with the auto-swapped
   * sickle in the hand the owner saw two sickles, one floating above-left of the character.
   * No-op without a gather bridge, without an avatar, or if already wired. The gather skill is
   * already on the wire (`gatherResult.gatheringSkill`) — nothing new is sent, and there is no
   * gather anim state, so OTHER players show no tool (EQP-025 §4, self-view only).
   */
  private attachGatherTool(): void {
    if (!this.gatherTool || !this.ownMesh || this.gatherToolUnsub) return;
    // The same opt-out hides a gather prop for a deliberate gear-free diagnostic.
    // No capture verb gathers today (`orbit_hold`/`walk_line` only), so this is a
    // future-proof consistency guard rather than a reachable capture case.
    if (captureEquipmentHidden()) return;
    // ms — how long the tool stays in hand after a gather resolves. A gather streak
    // re-shows + resets the timer, so the tool holds through consecutive harvests.
    const GATHER_TOOL_SHOW_MS = 1400;
    const source = this.gatherTool.gather;
    // Seed the baseline WITHOUT showing, so a pre-existing lastGather (from before
    // the avatar built) never fires the beat — only a subsequent NEW gather does.
    this.lastGatherSeen = source.get();
    const onChange = (): void => {
      const moment = source.get();
      if (moment === this.lastGatherSeen) return; // an unrelated store bump
      this.lastGatherSeen = moment;
      if (moment?.ok !== true) return; // failed/absent gather ⇒ no tool
      const skill = moment.gatheringSkill;
      if (skill == null || skill === '') return;
      this.equipmentRenderer?.setHeldTool(resolveGatherTool(skill));
      if (this.gatherToolTimer !== null) window.clearTimeout(this.gatherToolTimer);
      this.gatherToolTimer = window.setTimeout(() => {
        this.gatherToolTimer = null;
        this.equipmentRenderer?.setHeldTool(null);
      }, GATHER_TOOL_SHOW_MS);
    };
    this.gatherToolUnsub = source.subscribe(onChange);
  }

  /** Cancel the beat timer, unsubscribe, and give the hand back to the equipped item. */
  private teardownGatherTool(): void {
    if (this.gatherToolTimer !== null) {
      window.clearTimeout(this.gatherToolTimer);
      this.gatherToolTimer = null;
    }
    if (this.gatherToolUnsub) {
      this.gatherToolUnsub();
      this.gatherToolUnsub = null;
    }
    this.equipmentRenderer?.setHeldTool(null);
    this.lastGatherSeen = null;
  }

  /**
   * Present a one-shot action on an entity's rig (AP5.S2), or release its held
   * action with `null` (the respawn). Every caller is an event/input/store path
   * that must never throw into the frame loop, so this is the single guarded seam:
   * a no-op without a rig renderer, for any other negative id, and — inside the
   * renderer — for any entity without a rig (enemies, capsules). `entityId` is a RIG
   * key: the own rig's is `OWN_RIG_KEY`; an event's wire id goes through `rigKeyFor`.
   */
  private presentAction(
    entityId: number,
    action: RigAction | null,
    /** What the action is done with (weapon class / gather kind / craft kind); see actionClips.ts. */
    variant: string | null = null,
    /** AD-665: fit the clip to this many seconds (a gather channel). */
    fitSeconds?: number,
    /** AD-670: the attack combo step. */
    comboStep = 0,
  ): void {
    // Enemies: only `dead` arrives here for them (entityDied); creature ids and player
    // ids never collide, so each renderer ignores the other's entities.
    if (action === 'dead') this.presentCreatureAction(entityId, action);
    if (this.playerRigRenderer === null || (entityId < 0 && entityId !== OWN_RIG_KEY)) return;
    try {
      if (action === null) this.playerRigRenderer.clearAction(entityId);
      else this.playerRigRenderer.playAction(entityId, action, variant, fitSeconds, comboStep);
    } catch (error) {
      if (!this.animWarned) {
        this.animWarned = true; // once, not every trigger
        console.warn('[rig-anim] action presentation failed; rendering continues', error);
      }
    }
  }

  /** The rig key for a wire id: our body's is `OWN_RIG_KEY`, a remote's is its id. */
  private rigKeyFor(wireId: number): number {
    return wireId === this.ownWireId && this.ownEntityId >= 0 ? OWN_RIG_KEY : wireId;
  }

  /**
   * Present a one-shot on an enemy's animated proxy — the creature twin of
   * `presentAction`, equally guarded. A no-op without a creature renderer and for
   * any id that is not an animated creature (players, static proxies, cones).
   */
  private presentCreatureAction(entityId: number, action: RigAction): void {
    if (this.creatureRenderer === null || entityId < 0) return;
    this.guardCreature(() => this.creatureRenderer?.playAction(entityId, action));
  }

  /** Run one creature-presentation call so a throw never reaches the event/frame loop. */
  private guardCreature(present: () => unknown): void {
    try {
      present();
    } catch (error) {
      if (!this.animWarned) {
        this.animWarned = true;
        console.warn('[rig-anim] creature action failed; rendering continues', error);
      }
    }
  }

  /**
   * Drop a removed enemy's visuals. A rigged enemy holding its death clip lingers as
   * a corpse (the server deleted it the tick it died) and leaves the scene when the
   * creature renderer lets it go; anything else leaves at once, as before.
   */
  private retireEntityVisual(
    id: number,
    entity: { mesh: THREE.Object3D; healthBar?: THREE.Object3D },
  ): void {
    if (entity.healthBar) this.discard(entity.healthBar);
    this.playerRigRenderer?.remove(id); // E2.4.S2: drop any rig / in-flight build
    this.remoteSwings.delete(id); // AD-671
    this.dropRemoteEquipment(id);
    const mesh = entity.mesh;
    const lingers = this.creatureRenderer?.retire(id, () => this.discard(mesh)) ?? false;
    if (!lingers) this.discard(mesh);
  }

  /**
   * Play `action` on the own rig for every NEW successful local result (AP5.S2).
   * Identity dedupe: the store replaces the slice per applied result, so a stale
   * value never re-triggers (the E2.2.S3 gather-tool pattern); the baseline is
   * seeded WITHOUT playing so a result from before construction is inert. Returns
   * the unsubscribe.
   */
  private watchActionMoment(source: RigActionMomentSource, action: RigAction): () => void {
    let seen = source.get();
    return source.subscribe(() => {
      const moment = source.get();
      if (moment === seen) return; // an unrelated store bump
      const previous = seen;
      seen = moment;
      // AD-665: a gather channel that ends EARLY (refused or interrupted — its answer came
      // before its length ran out) takes its clip with it; a completed one has finished.
      if (moment === null && action === 'gather' && previous?.durationMs !== undefined) {
        const startedAt = (previous as { startedAtMs?: number }).startedAtMs ?? 0;
        const early = performance.now() < startedAt + previous.durationMs - 100;
        if (early && this.playerRigRenderer?.activeAction(OWN_RIG_KEY) === 'gather') {
          this.presentAction(OWN_RIG_KEY, null);
        }
        return;
      }
      if (moment?.ok !== true) return; // a failed/absent result plays nothing
      // 2026-09-23: the motion variant — the gather skill's kind (chop/mine/harvest)
      // or the one craft motion; `null` (unknown skill) keeps the generic clip.
      const variant =
        action === 'gather'
          ? gatherKindForSkill(moment.gatheringSkill)
          : action === 'craft'
            ? DEFAULT_CRAFT_KIND
            : null;
      // AD-665: a gather moment is the CHANNEL — its clip is fitted to end on the yield.
      const fit =
        typeof moment.durationMs === 'number' && moment.durationMs > 0
          ? moment.durationMs / 1000
          : undefined;
      if (this.ownEntityId >= 0) this.presentAction(OWN_RIG_KEY, action, variant, fit);
    });
  }

  /**
   * The own attack's weapon class (2026-09-23; actionClips.ts): the equipped
   * mainhand's held archetype (`items[].presentation.model`), else the offhand's.
   * Read from the equipped SET + content, not from the rendered meshes, so it holds
   * with equipment hidden or still loading. `null` (no bridge, unarmed, an unbound
   * or unknown weapon) ⇒ the generic swing. Never throws into the input path.
   */
  /**
   * q-demo-vfx-pass: where an effect anchor stands this frame (feet), or null when it is
   * not drawn. Under the DEV gallery every non-self anchor is a point beside the avatar, so
   * each effect is filmed in the same place on every tier.
   */
  private vfxPoint(anchor: VfxAnchor): WorldPoint | null {
    const own = this.ownMesh;
    const selfAnchor = anchor.at === 'entity' && anchor.id === this.ownWireId;
    if (
      this.vfxGallery !== null &&
      anchor.at !== 'self' &&
      anchor.at !== 'point' &&
      !selfAnchor &&
      own
    ) {
      const g = this.vfxGallerySpot();
      return g;
    }
    switch (anchor.at) {
      case 'self':
        return own ? { x: own.position.x, y: own.position.y, z: own.position.z } : null;
      case 'entity': {
        if (anchor.id === this.ownWireId && own)
          return { x: own.position.x, y: own.position.y, z: own.position.z };
        const e = this.entities.get(anchor.id);
        if (e) return { x: e.mesh.position.x, y: e.mesh.position.y, z: e.mesh.position.z };
        const d = this.vfxLastDeath;
        if (d !== null && d.id === anchor.id)
          return { x: d.x, y: seatY(this.ground, d.x, d.z, 0), z: d.z };
        return null;
      }
      case 'node': {
        const n = this.lastNodes?.find((node) => node.id === anchor.id);
        return n ? { x: n.x, y: seatY(this.ground, n.x, n.z, 0), z: n.z } : null;
      }
      case 'point':
        return { x: anchor.x, y: seatY(this.ground, anchor.x, anchor.z, 0), z: anchor.z };
    }
  }

  /** DEV gallery: a fixed spot beside the avatar, to the camera's right. */
  private vfxGallerySpot(): WorldPoint | null {
    const own = this.ownMesh;
    if (!own) return null;
    const cam = this.orbit.camera;
    const right = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld, 0);
    right.y = 0;
    if (right.lengthSq() < 1e-6) right.set(1, 0, 0);
    right.normalize();
    // Beside the avatar and a step toward the camera, inside a portrait phone's narrow view.
    const back = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld, 2);
    back.y = 0;
    if (back.lengthSq() < 1e-6) back.set(0, 0, 1);
    back.normalize();
    const x = own.position.x + right.x * 0.9 + back.x * 1.2;
    const z = own.position.z + right.z * 0.9 + back.z * 1.2;
    return { x, y: seatY(this.ground, x, z, 0), z };
  }

  /** q-demo-vfx-pass, per frame: the Dodge edge + trail, the DEV gallery, then the pool. */
  private vfxFrame(now: number, dtMs: number): void {
    const vfx = this.vfx;
    if (vfx === null) return;
    try {
      const dtS = dtMs / 1000;
      const dodgeMs = this.predicted.dodgeRemainingMs;
      const own = this.ownMesh;
      if (dodgeMs > 0 && this.vfxPrevDodgeMs <= 0)
        gameFeedback.emit({
          kind: 'dodge',
          dirX: this.predicted.dodgeDirX,
          dirZ: this.predicted.dodgeDirZ,
        });
      if (dodgeMs > 0 && own) vfx.trail(own.position.x, own.position.y, own.position.z, dtS);
      this.vfxPrevDodgeMs = dodgeMs;
      if (this.vfxGallery !== null && own && this.ownWireId >= 0) this.vfxGalleryTick(now);
      const canvas = this.renderer.domElement;
      vfx.frame(dtS, this.orbit.camera, canvas.clientWidth || 1, canvas.clientHeight || 1);
    } catch (error) {
      if (!this.vfxWarned) {
        this.vfxWarned = true;
        console.warn('[vfx] frame failed; rendering continues', error);
      }
    }
  }

  private vfxWarned = false;

  /** DEV gallery: fire the named effect(s), let them bloom briefly, then freeze; repeat. */
  private vfxGalleryTick(now: number): void {
    const vfx = this.vfx;
    const name = this.vfxGallery;
    if (vfx === null || name === null) return;
    if (now >= this.vfxGalleryAtMs) {
      vfx.pool.clear();
      vfx.setHold(0);
      const spot = this.vfxGallerySpot();
      const ctx = {
        selfId: this.ownWireId,
        targetId: this.ownWireId + 1_000_000, // no such body: the gallery spot stands in
        x: spot?.x ?? 0,
        z: spot?.z ?? 0,
        nodeId: -1,
      };
      const names = name === 'all' ? VFX_GALLERY_NAMES : [name];
      for (const n of names) for (const ev of galleryEvents(n, ctx)) vfx.handle(ev);
      // The Dodge trail is per-frame in play; the gallery lays a short trail at once.
      if ((name === 'dodge' || name === 'all') && this.ownMesh) {
        const o = this.ownMesh.position;
        const r = new THREE.Vector3().setFromMatrixColumn(this.orbit.camera.matrixWorld, 0);
        for (let k = 1; k <= 5; k += 1)
          vfx.afterimage(o.x - r.x * k * 0.28, o.y, o.z - r.z * k * 0.28);
      }
      this.vfxGalleryAtMs = now + 6000;
      this.vfxGalleryFreezeAtMs = now + galleryFreezeMs(name);
    } else if (this.vfxGalleryFreezeAtMs > 0 && now >= this.vfxGalleryFreezeAtMs) {
      vfx.setHold(1);
      this.vfxGalleryFreezeAtMs = 0;
    }
  }

  /**
   * A damaging ability swings the caster's rig (AD-670): the heavy clip for a heavy
   * ability when the class has one, else the class's attack. Remote casters play the
   * generic swing (their weapon is not on the wire, as for remote attacks). The own swing
   * also occupies the SwingGate, so the next attack press chains after it, never over it.
   */
  private presentAbility(sourceId: number, abilityId: string): void {
    if (abilityMotion(abilityId) === null || this.playerRigRenderer === null || sourceId < 0)
      return;
    try {
      const self = sourceId === this.ownWireId;
      const wall = this.playerRigRenderer.playAbility(
        this.rigKeyFor(sourceId),
        abilityId,
        self ? this.ownWeaponClass() : this.remoteWeaponClass(sourceId),
      );
      if (self && wall !== null) this.swingGate.begin(performance.now(), wall, 0);
    } catch (error) {
      if (!this.animWarned) {
        this.animWarned = true;
        console.warn('[rig-anim] ability presentation failed; rendering continues', error);
      }
    }
  }

  /** Per remote player: the equipment renderer drawing what it holds (AD-671). */
  private readonly remoteEquipment = new Map<number, EquipmentRenderer>();
  /** Per remote player: its settled rig root (for the grip morph), or null on a capsule. */
  private readonly remoteRigRoots = new Map<number, THREE.Object3D | null>();

  /**
   * Upgrade a remote player's capsule to the rig (E2.4.S2) and, once it settles, seat its
   * held weapons on the rig's bone sockets (AD-671) — the same re-seat the own avatar gets.
   */
  /**
   * Re-measure an entity's health-bar height from the model that now draws it (the settled
   * creature proxy or player rig — not held equipment, which would lift the bar on a raised
   * blade). q-enemy-healthbar-above-model: the bar sits above the head of EVERY model.
   */
  private measureHealthBar(id: number, model: THREE.Object3D): void {
    const entity = this.entities.get(id);
    if (entity === undefined) return;
    entity.barY = healthBarLocalY(model, entity.mesh);
  }

  private upgradeRemotePlayer(
    id: number,
    group: THREE.Object3D,
    appearance: CharacterAppearance,
  ): void {
    this.syncRemoteEquipment(id); // capsule sockets until the rig settles
    // AD-833: a summoned image (`summonOf`) reads as an image from its first frame to its rig.
    const image = isSummonedImage(this.entities.get(id)?.latest ?? {});
    if (image) applyMirrorImageLook(group);
    this.playerRigRenderer?.upgrade(id, group, appearance, (root) => {
      this.remoteRigRoots.set(id, root);
      if (root !== null) this.measureHealthBar(id, root);
      if (image && root !== null) applyMirrorImageLook(root);
      const renderer = this.remoteEquipment.get(id);
      if (root === null || renderer === undefined) return;
      renderer.setPlacement(
        rigSocketPlacement(
          (name) => root.getObjectByName(name) ?? null,
          group,
          skinnedEquipmentBinder(root),
        ),
      );
    });
  }

  /**
   * Draw what a remote player holds (AD-671, owner 2026-09-25: other players held no
   * weapon — equipment was self-view only). One `EquipmentRenderer` per remote, fed from
   * its wire `held` item ids; created lazily, re-synced on each `held` delta, disposed
   * with the entity. The capture opt-out hides it exactly as it hides the own gear.
   */
  private syncRemoteEquipment(id: number): void {
    const entity = this.entities.get(id);
    if (!this.equipment || entity === undefined || entity.latest.kind !== 'player') return;
    if (captureEquipmentHidden()) return;
    const held = entity.latest.held;
    let renderer = this.remoteEquipment.get(id);
    if (renderer === undefined) {
      if (held === undefined) return; // nothing to draw yet
      renderer = new EquipmentRenderer({
        target: entity.mesh,
        content: this.equipment.content,
        pending: { sink: this.pendingAssets, anchor: objectAnchor(entity.mesh) }, // AD-794
        gltfLoad: createThreeGltfLoader({ renderer: this.renderer }),
        httpBase: assetHttpBase(),
        onMainhandModelChange: (model, mesh) => {
          const root = this.remoteRigRoots.get(id);
          if (root) applyHeldGripMorphs(root, model, mesh);
        },
      });
      this.remoteEquipment.set(id, renderer);
      const root = this.remoteRigRoots.get(id);
      if (root) {
        const group = entity.mesh;
        renderer.setPlacement(
          rigSocketPlacement(
            (name) => root.getObjectByName(name) ?? null,
            group,
            skinnedEquipmentBinder(root),
          ),
        );
      }
    }
    renderer.sync(
      (['mainhand', 'offhand'] as const)
        .filter((slot) => held?.[slot] !== undefined)
        .map((slot) => ({
          id: `remote-${id}-${slot}`,
          contentId: held?.[slot] as string,
          slot,
        })) as unknown as Parameters<EquipmentRenderer['sync']>[0],
    );
  }

  /** Drop a departing remote's held-weapon renderer + rig root (AD-671). */
  private dropRemoteEquipment(id: number): void {
    this.remoteEquipment.get(id)?.dispose();
    this.remoteEquipment.delete(id);
    this.remoteRigRoots.delete(id);
  }

  /** Per remote player: the last swing's end + step, for combo chaining (AD-671). */
  private readonly remoteSwings = new Map<number, SwingGate>();

  /** A remote player's weapon class from its wire `held` items (AD-671), or `null`. */
  private remoteWeaponClass(entityId: number): WeaponClass | null {
    const held = this.entities.get(entityId)?.latest.held;
    const bridge = this.equipment;
    if (held === undefined || bridge === undefined) return null;
    try {
      return weaponClassForEquipped(
        (['mainhand', 'offhand'] as const)
          .filter((slot) => held[slot] !== undefined)
          .map((slot) => ({
            slot,
            model: gripArchetype(bridge.content.itemPresentation(held[slot] as string)),
          })),
      );
    } catch {
      return null;
    }
  }

  /**
   * A remote player's basic attack (AD-671): its class clip, and the next combo step when
   * the hit follows its previous swing within the chain grace — the server broadcasts a
   * remote's `attack` at most once per cooldown, so a steady exchange chains exactly like
   * the own mash. Unknown weapon ⇒ the generic swing, as before.
   */
  private presentRemoteAttack(entityId: number): void {
    if (this.playerRigRenderer === null) {
      this.presentAction(entityId, 'attack');
      return;
    }
    const variant = this.remoteWeaponClass(entityId);
    let gate = this.remoteSwings.get(entityId);
    if (gate === undefined) {
      gate = new SwingGate(PLAYER_ATTACK_COOLDOWN_SECONDS, 1 / TICK_RATE);
      this.remoteSwings.set(entityId, gate);
    }
    const now = performance.now();
    const step = gate.stepFor(now) % comboLength(variant);
    this.presentAction(entityId, 'attack', variant, undefined, step);
    const wall = this.playerRigRenderer.actionWallSeconds(entityId, 'attack', variant, step);
    gate.begin(now, wall ?? PLAYER_ATTACK_COOLDOWN_SECONDS, step);
  }

  /**
   * Own basic attacks sent vs own swings the rig actually STARTED (scene dump, DEV): a lane
   * tells "sent but never shown" from a frame-independent count
   * (q-tap-attack-after-travel-no-swing).
   */
  private ownAttacksSent = 0;
  private ownSwingsShown = 0;

  /** Whether the own rig is still presenting a swing (the gate's `showing`). */
  private ownSwingShowing(): boolean {
    return this.playerRigRenderer?.activeAction(OWN_RIG_KEY) === 'attack';
  }

  /**
   * Present the own swing now and tell the gate how long it owns the pose. A swing
   * that does not start (moving — movement wins; dead; no rig) records nothing, so the
   * next accepted press starts one at once.
   */
  private startOwnSwing(now: number): void {
    const variant = this.ownWeaponClass();
    // AD-670: a swing chained onto the last one plays the next combo step; a pause resets.
    const step = this.swingGate.stepFor(now) % comboLength(variant);
    if (this.ownEntityId >= 0) this.presentAction(OWN_RIG_KEY, 'attack', variant, undefined, step);
    if (!this.ownSwingShowing()) return;
    this.ownSwingsShown += 1;
    const wall = this.playerRigRenderer?.actionWallSeconds(OWN_RIG_KEY, 'attack', variant, step);
    this.swingGate.begin(now, wall ?? PLAYER_ATTACK_COOLDOWN_SECONDS, step);
  }

  private ownWeaponClass(): WeaponClass | null {
    const bridge = this.equipment;
    if (bridge === undefined) return null;
    try {
      return weaponClassForEquipped(
        bridge.equipped.get().map((item) => ({
          slot: item.slot,
          model: gripArchetype(bridge.content.itemPresentation(item.contentId)),
        })),
      );
    } catch {
      return null;
    }
  }

  /**
   * Upgrade the freshly-built own avatar to the player rig (E2.4.S2), applying the
   * self appearance retained at onReady. On rig success, re-seat self-view equipment
   * onto the rig's CHR-026 §4.2 bone sockets — a bone miss (e.g. the interim 24-joint
   * rig) falls back per-socket to the capsule transform on the group, so equipment
   * placement is unchanged from E2.2 in that case. No-op without a rig renderer or an
   * avatar. Graceful: no rig pack / load error keeps the capsule and leaves equipment
   * on its capsule sockets (E2.2 behaviour unchanged).
   */
  private attachPlayerRig(): void {
    if (!this.playerRigRenderer || !this.ownMesh) return;
    const group = this.ownMesh;
    this.playerRigRenderer.upgrade(OWN_RIG_KEY, group, this.ownAppearance, (root) => {
      this.ownRigRoot = root;
      // The own rig is on the critical path; a capsule (no rig pack YET, before the content
      // pack lands) is not, and leaves the release to the gate's fallback.
      if (root !== null) this.firstFrameGate?.markCriticalSettled();
      if (root === null) return; // capsule fallback ⇒ equipment stays on its capsule sockets
      applyHeldGripMorphs(root, this.ownMainhandModel, this.ownMainhandMesh);
      // Re-seat equipment onto the rig's bones; a missing bone falls back per-socket
      // to the capsule transform on the avatar group (the interim rig case).
      const placement = rigSocketPlacement(
        (name) => root.getObjectByName(name) ?? null,
        group,
        skinnedEquipmentBinder(root),
      );
      this.equipmentRenderer?.setPlacement(placement);
    });
  }

  /**
   * Subscribe to a post-start renderer loss (one listener — App's). A loss that
   * already happened is delivered at once, so subscribing late loses nothing.
   */
  onRendererLost(listener: (info: RendererLossInfo) => void): void {
    this.rendererLostListener = listener;
    if (this.lostRenderer !== null) listener(this.lostRenderer);
  }

  /** The loss, if the renderer has died since construction; else null. */
  get rendererLost(): RendererLossInfo | null {
    return this.lostRenderer;
  }

  private handleRendererLost(info: RendererLossInfo): void {
    this.lostRenderer = info;
    // Stop driving a dead renderer. Resources stay for dispose(), which the
    // reload the player is asked for (or App's unmount) runs as usual.
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
    this.rendererLostListener?.(info);
  }

  /** Live enemies as the soft-target picker reads them (latest snapshot positions). */
  private hostileViews(): HostileView[] {
    const out: HostileView[] = [];
    for (const e of this.entities.values()) {
      const s = e.latest;
      if (s.kind !== 'enemy') continue;
      out.push(
        s.health === undefined
          ? { id: s.id, x: s.x, z: s.z }
          : { id: s.id, x: s.x, z: s.z, health: s.health },
      );
    }
    return out;
  }

  /** The scene dump's combat block (DEV only — the dump hook is compiled out of production). */
  private combatDump(): Record<string, unknown> {
    const self = { x: this.predicted.x, z: this.predicted.z };
    const enemies = [...this.entities.values()]
      .filter((e) => e.latest.kind === 'enemy')
      .map(({ latest: s, mesh }) => ({
        id: s.id,
        name: s.name,
        x: Math.round(s.x * 10) / 10,
        z: Math.round(s.z * 10) / 10,
        health: s.health ?? null,
        healthMax: s.healthMax ?? null,
        dist: Math.round(Math.hypot(s.x - self.x, s.z - self.z) * 10) / 10,
        // AD-817: where a click/tap lands on this body (canvas CSS px, mid-body), so a
        // lane run can press the REAL pointer path on it; null = behind the camera.
        screen: this.screenPointOf(mesh.position, PICK_BODY_HEIGHT_M / 2),
      }))
      .filter((e) => e.dist <= 30)
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 8);
    // The wind-up rings on the ground now (what a player SEES): whether each covers us and how
    // long until it lands, so a lane can play Dodge the way a phone player would (AD-828).
    const estTick = this.tickOffset === null ? null : performance.now() / TICK_MS + this.tickOffset;
    // AD-833: summoned images (the mirror-image decoy) — where they stand, and per ring whether it
    // covers one, so a lane can tell an image holding aggro from the avatar holding it.
    const images = [...this.entities.values()]
      .filter((e) => isSummonedImage(e.latest))
      .map(({ latest: s }) => ({
        id: s.id,
        summonOf: s.summonOf ?? null,
        x: Math.round(s.x * 10) / 10,
        z: Math.round(s.z * 10) / 10,
        dist: Math.round(Math.hypot(s.x - self.x, s.z - self.z) * 10) / 10,
      }));
    const telegraphs = [...this.telegraphs.entries()].map(([sourceId, v]) => ({
      sourceId,
      covers: telegraphCovers(v.shape, self),
      coversImage: images.some((i) => telegraphCovers(v.shape, i)),
      msLeft: estTick === null ? null : Math.round((v.resolveTick - estTick) * TICK_MS),
    }));
    return {
      softTarget: this.softTarget(performance.now()),
      approaching: this.attackApproach?.targetId ?? null,
      explicitTarget: this.explicit.id,
      // q-tap-attack-after-travel-no-swing: the own rig's one-shot now, and the running counts
      // of basic attacks sent and swings the rig started — "sent, never shown" is the bug.
      selfId: this.ownWireId,
      ownAction: this.playerRigRenderer?.activeAction(OWN_RIG_KEY) ?? null,
      ownAttacksSent: this.ownAttacksSent,
      ownSwingsShown: this.ownSwingsShown,
      enemies,
      images,
      telegraphs,
      dodgeReady: this.predicted.dodgeCooldownMs <= 0,
    };
  }

  /**
   * The scene dump's nav block (DEV only): the avatar's feet and health, the camera's view-projection
   * (column-major, three.js `elements`) so a lane can project any ground point to the canvas
   * px a tap must land on, and the zone's gather nodes as the join ack placed them.
   */
  private navDump(): Record<string, unknown> {
    const cam = this.orbit.camera;
    cam.updateMatrixWorld();
    const pv = new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    const feet = this.ownMesh?.position ?? null;
    return {
      self: feet
        ? {
            x: feet.x,
            y: feet.y,
            z: feet.z,
            health: this.status.health,
            healthMax: this.status.healthMax,
          }
        : null,
      viewProjection: pv.elements.slice(),
      nodes: (this.lastNodes ?? []).map((n) => ({
        id: n.id,
        resource: n.resource,
        x: n.x,
        z: n.z,
      })),
    };
  }

  /** Canvas CSS px of a world point `lift` metres above `at`, or null when behind the camera (DEV dump). */
  private screenPointOf(at: THREE.Vector3, lift: number): { x: number; y: number } | null {
    const rect = this.canvasRect();
    const cam = this.orbit.camera;
    cam.updateMatrixWorld();
    const v = this.pickScratch.set(at.x, at.y + lift, at.z).project(cam);
    if (v.z < -1 || v.z > 1) return null;
    return {
      x: Math.round(((v.x + 1) / 2) * rect.width),
      y: Math.round(((1 - v.y) / 2) * rect.height),
    };
  }

  /** The soft target from the predicted body (combat.md §Control model). */
  softTarget(nowMs: number): SoftTargetPick | null {
    this.threats.prune(nowMs);
    return pickSoftTarget({
      self: { x: this.predicted.x, z: this.predicted.z },
      hostiles: this.hostileViews(),
      threats: this.threats,
      nowMs,
      explicitTargetId: this.explicit.id,
    });
  }

  /**
   * An attack press (F, the bar button, a click/tap on a hostile): with no hostile in the
   * soft-target radius it is sent as-is (the server's refusal is the truthful answer);
   * otherwise the walk-to/face/swing below runs until it swings or is abandoned.
   */
  private advanceAttack(now: number): void {
    // Steering (keys or the on-screen stick) drops the explicit target (combat.md §Control model, AD-817).
    if (this.input.steering) this.explicit.clear();
    if (this.input.consumeAttack()) {
      this.endAttackApproach();
      this.arrivalSwingDueMs = null; // this press presents its own swing
      const plan = planAttack({ x: this.predicted.x, z: this.predicted.z }, this.softTarget(now));
      if (plan.kind === 'send') {
        this.fireSwing(now);
        return;
      }
      this.attackApproach = { targetId: plan.targetId, startedMs: now, walking: false };
    }
    this.presentArrivalSwing(now);
    const approach = this.attackApproach;
    if (approach === null) return;
    // The player steering (keys or stick) takes the body back.
    if (this.input.steering) {
      this.endAttackApproach();
      return;
    }
    const target = this.hostileViews().find((h) => h.id === approach.targetId);
    const step = stepApproach({
      self: { x: this.predicted.x, z: this.predicted.z },
      server: this.ownAuthoritative,
      target,
      startedMs: approach.startedMs,
      nowMs: now,
    });
    switch (step.kind) {
      case 'abandon':
        this.endAttackApproach();
        return;
      case 'walk':
        this.input.setMoveTarget(step.x, step.z);
        approach.walking = true;
        return;
      case 'face':
        if (approach.walking) this.input.clearMoveTarget();
        approach.walking = false;
        this.input.setFacing(step.facing);
        if (step.swing) {
          this.attackApproach = null;
          // The arrival frame: the body still runs on last tick's velocity, so the rig — where
          // movement wins — refuses the swing the server is resolving right now. Hold its
          // presentation until the body has stopped (q-tap-attack-after-travel-no-swing).
          if (this.fireSwing(now) === 'start' && !this.ownSwingShowing()) {
            this.arrivalSwingDueMs = now + PLAYER_ATTACK_COOLDOWN_SECONDS * 1000;
          }
        }
        return;
    }
  }

  /**
   * The walk-to's swing, sent on arrival but refused by the still-running rig
   * (q-tap-attack-after-travel-no-swing; owner playtest 2026-10-29: "doesn't play the attack
   * animation once the enemy is reached if the character has to travel"). The approach has
   * already cleared its move target, so the body stops on the next input tick; the swing is
   * presented then. Presentation only — the attack was sent once and the gate already counted
   * the press. Dropped when the player steers (they took the body back), on a new press (it
   * presents its own), and after one swing cadence (the moment has passed).
   */
  private arrivalSwingDueMs: number | null = null;

  private presentArrivalSwing(now: number): void {
    const due = this.arrivalSwingDueMs;
    if (due === null) return;
    if (this.input.steering || now > due) {
      this.arrivalSwingDueMs = null;
      return;
    }
    this.startOwnSwing(now);
    if (this.ownSwingShowing()) this.arrivalSwingDueMs = null;
  }

  private endAttackApproach(): void {
    if (this.attackApproach?.walking) this.input.clearMoveTarget();
    this.attackApproach = null;
  }

  /**
   * Send one server-authoritative swing. AP5.S2: swing the own rig NOW (prediction —
   * no round-trip wait); the server's echoed `attack` event for self is ignored in
   * onEvent, so it cannot double-fire. Always sent — the server's cooldown is
   * authoritative. The swing gate decides only what the own rig SHOWS: a swing is
   * never cut back to frame 0 by the next press (q-player-attack-no-restart).
   */
  private fireSwing(now: number): SwingVerdict {
    this.conn.sendAttack();
    this.ownAttacksSent += 1;
    const verdict = this.swingGate.press(now, this.ownSwingShowing());
    if (verdict === 'start') this.startOwnSwing(now);
    return verdict;
  }

  start(): void {
    if (this.running || this.lostRenderer !== null) return;
    this.running = true;
    this.lastFrameMs = performance.now();
    const loop = (): void => {
      if (!this.running) return;
      this.frame();
      this.rafHandle = requestAnimationFrame(loop);
    };
    this.rafHandle = requestAnimationFrame(loop);
  }

  private frame(): void {
    const now = performance.now();
    const dt = Math.min(100, now - this.lastFrameMs);
    this.lastFrameMs = now;

    // O-025: while the dungeon preview owns the camera/controls, swallow the game's
    // fixed-rate move sampling entirely (client-local; the server player stands still).
    const dungeonBlocksInput = this.dungeonLayer?.blocksGameInput() === true;
    // Fixed-rate input sampling + prediction (one step per sent input).
    //
    // The `5 * TICK_MS` cap below is UNREACHABLE, and saying so is the point
    // (AD-244 gate finding C-3). `dt` is already clamped to 100 ms above, and the
    // while-loop drains the accumulator below TICK_MS (66.67 ms) every frame — so
    // the value entering the min() is always < 166.67 ms, never the 333.3 ms the
    // cap describes. A real client's maximum burst is therefore **2 inputs per
    // frame, not 5**. It is kept as defence-in-depth against a future change to
    // the `dt` clamp, NOT as the operative bound, and it must not be cited as
    // "the same number as the server's INPUT_CREDIT_MAX": the server's bank of 5
    // is a ceiling with headroom over this, which is why the honest client is not
    // throttled — with more margin than the original AD-244 rationale claimed,
    // not less. See docs/architecture/networking.md §"The input ration (AD-244)".
    if (this.conn.currentPhase === 'playing' && !dungeonBlocksInput) {
      // Basic attack (P4.6) through the soft target (combat.md §Control model) — BEFORE the
      // input sampling, so a face / walk-to it sets is carried by this frame's inputs.
      this.tapControls.tick(now); // AD-817: a press held past HOLD_PRESS_MS becomes a hold
      this.advanceAttack(now);
      this.inputAccumMs = Math.min(this.inputAccumMs + dt, 5 * TICK_MS);
      while (this.inputAccumMs >= TICK_MS) {
        this.inputAccumMs -= TICK_MS;
        const input = this.input.sample(this.orbit.yaw, this.predicted.x, this.predicted.z);
        const seq = this.conn.sendInput(input);
        if (seq !== null) {
          this.predicted = stepMovement(this.predicted, input, TICK_MS, this.bounds, this.collider);
          this.pending.push({ seq, input });
          if (this.pending.length > 150) this.pending.shift();
        }
      }
      if (this.swingGate.poll(now, this.ownSwingShowing())) this.startOwnSwing(now);
    }
    if (!this.input.hasMoveTarget) this.moveMarker.visible = false;
    this.updateExplicitTarget();

    // Correction offset decays smoothly to zero.
    const decay = Math.min(1, dt / CORRECTION_SMOOTH_MS);
    this.correction.x *= 1 - decay;
    this.correction.z *= 1 - decay;

    // Own mesh at predicted + correction.
    if (this.ownEntityId >= 0) {
      if (!this.ownMesh) {
        this.ownMesh = this.buildEntityMesh(
          {
            id: this.ownEntityId,
            kind: 'player',
            name: 'you',
            x: this.predicted.x,
            z: this.predicted.z,
            vx: 0,
            vz: 0,
            facing: this.predicted.facing,
            anim: 'idle',
            dodgeRemainingMs: 0,
            dodgeCooldownMs: 0,
          },
          true,
        );
        // E2.2.S2: the avatar group exists now — attach self-view equipment meshes.
        this.attachEquipment();
        // E2.2.S3: and the gather beat (the tool drawn in the hand during a local gather).
        this.attachGatherTool();
        // E2.4.S2: upgrade the own capsule to the skinned rig (self appearance), and
        // on success re-seat equipment onto the rig's bone sockets.
        this.attachPlayerRig();
      }
      const ownX = this.predicted.x + this.correction.x;
      const ownZ = this.predicted.z + this.correction.z;
      this.ownMesh.position.set(ownX, seatY(this.ground, ownX, ownZ, 0), ownZ);
      // q-walk-turn-restarts-stride (owner, Desk #849): the body TURNS to a new heading
      // over a few frames instead of snapping — presentation only, the simulated facing is
      // untouched.
      this.ownMesh.rotation.y = easeYaw(this.ownMesh.rotation.y, this.predicted.facing, dt);
    }

    // Remote entities render interpDelayMs in the past (INTERP_DELAY_MS at 15 Hz; D1).
    if (this.tickOffset !== null) {
      const estTick = now / TICK_MS + this.tickOffset;
      const renderTick = estTick - this.interpDelayMs / TICK_MS;
      for (const entity of this.entities.values()) {
        const sample = entity.buffer.sample(renderTick);
        if (sample) {
          entity.mesh.position.set(sample.x, seatY(this.ground, sample.x, sample.z, 0), sample.z);
          entity.mesh.rotation.y = easeYaw(entity.mesh.rotation.y, sample.facing, dt);
        }
        if (entity.healthBar && entity.healthFill) {
          const max = entity.latest.healthMax ?? 1;
          const cur = entity.latest.health ?? max;
          const ratio = max > 0 ? Math.max(0, Math.min(1, cur / max)) : 0;
          const pos = entity.mesh.position;
          // Hide at full (clutter) and at zero (the entity is dying/leaving);
          // the E1.5 capture sub-flag can also force all bars off for beauty shots.
          entity.healthBar.visible = this.healthBarsVisible && ratio > 0 && ratio < 1;
          entity.healthBar.position.set(
            pos.x,
            seatY(this.ground, pos.x, pos.z, entity.barY),
            pos.z,
          );
          entity.healthBar.quaternion.copy(this.orbit.camera.quaternion);
          entity.healthFill.scale.x = ratio;
          (entity.healthFill.material as THREE.MeshBasicMaterial).color.setHex(
            ratio > 0.5 ? 0x5fd16b : ratio > 0.25 ? 0xe6b450 : 0xff5d6c,
          );
        }
      }
      // Telegraph fill animation against server ticks.
      for (const visual of this.telegraphs.values()) {
        const span = Math.max(1, visual.resolveTick - visual.startTick);
        const progress = Math.min(1, Math.max(0.01, (estTick - visual.startTick) / span));
        visual.setProgress(progress);
        if (estTick > visual.resolveTick + 15) {
          this.discard(visual.group);
          // Defensive cleanup if the resolve event was missed.
          for (const [id, v] of this.telegraphs) if (v === visual) this.telegraphs.delete(id);
        }
      }
    }

    // Camera follows the own character.
    const target = this.ownMesh ? this.ownMesh.position : new THREE.Vector3();
    // E1.5 slow-orbit: gentle presentation-only yaw drift for capture shots
    // (within the existing camera bounds — drift touches yaw only, never dist/pitch).
    // W42 capture framing: resolve once the own avatar stands in the zone (never while a
    // `__GC_DEVCAM` move owns the boom), then drift only within the safe arc.
    // W42 re-render: a framing is only valid where it was resolved. The northwatch delver
    // was killed mid-hold and respawned 8 m from the west edge while the orbit kept the
    // checkpoint's arc — re-frame the moment the avatar moves or the pose shows void.
    if (
      this.slowOrbit &&
      this.framing !== null &&
      this.framingAnchor !== null &&
      !this.framingDirty &&
      this.ownMesh &&
      framingStale(
        this.framingAnchor,
        target,
        this.orbit,
        this.framing,
        this.framingGround(),
        this.framingView(),
      )
    ) {
      this.framingDirty = true;
    }
    if (this.framingDirty && this.ownMesh && !this.cameraMove) this.resolveCaptureFraming(target);
    if (this.slowOrbit) {
      if (this.framing !== null && !this.cameraMove) {
        const step = stepFramedOrbit(
          this.orbit.yaw,
          slowOrbitYawDelta(dt),
          this.framing,
          this.framingDir,
        );
        this.orbit.yaw = step.yaw;
        this.framingDir = step.dir;
      } else this.orbit.driftYaw(slowOrbitYawDelta(dt));
    }
    if (this.cameraMove) {
      const { move, from, startMs } = this.cameraMove;
      const elapsed = (now - startMs) / 1000;
      this.orbit.applyPose(cameraMovePose(from, move, elapsed));
      if (cameraMoveDone(move, elapsed)) this.cameraMove = null;
    }
    // O-025: the dungeon layer runs its pure lifecycle + glow off the local player
    // position each frame; while the explorer is active IT owns the camera, so the
    // orbit update is skipped (restored on exit).
    this.dungeonLayer?.frame(dt, this.predicted.x, this.predicted.z, now);
    this.dungeonEntrances?.frame(now, this.predicted.x, this.predicted.z);
    this.dungeonRunClient?.frame(this.predicted.x, this.predicted.z); // slice 2d: entrance in reach
    this.dungeonRunScene?.frame(); // slice 2e: world roots stay hidden while a run is drawn
    // The body is in the instance frame for the whole run (drawn or not): no world prompts.
    this.status.inDungeonRun = this.dungeonRunClient?.run != null;
    // AD-706: inside a drawn run the boom is lifted (render pitch, eased) and pulled
    // in so no wall stands between the lens and the avatar — the run's own walls
    // (`this.collider` is the run's while it is drawn); never a zoom/pitch change.
    const runShapes = this.dungeonRunScene?.active ? this.collider?.shapes : undefined;
    let renderDist: number | undefined;
    let renderPitch: number | undefined;
    if (runShapes && this.runWallTop !== null) {
      const top = this.runWallTop;
      const lift = runLiftPitch(target, this.orbit, runShapes, top, CAMERA_BOUNDS.MAX_PITCH);
      this.runPitch = easeRunPitch(this.runPitch ?? lift, lift, dt);
      renderPitch = Math.max(this.orbit.pitch, this.runPitch);
      renderDist = runBoomDistance(target, { ...this.orbit, pitch: renderPitch }, runShapes, top);
    } else {
      this.runPitch = null;
    }
    // q-follow-camera-vs-relief: over terrain relief the boom's pitch is taken
    // relative to the slope ahead and lifted clear of the hill behind (eased), so a
    // hillside cannot fill the frame. Flat ground (no heightfield) is untouched.
    let basePitch: number | undefined;
    if (!runShapes && this.ground.heightfield !== null) {
      const wanted = terrainRenderPitch(target, this.orbit, this.ground.heightAt);
      this.terrainPitch = easeTerrainPitch(this.terrainPitch ?? wanted, wanted, dt);
      basePitch = this.terrainPitch;
    } else {
      this.terrainPitch = null;
    }
    if (!dungeonBlocksInput) this.orbit.update(target, renderDist, renderPitch, basePitch);
    // q-combat-game-feel: trauma shake, a pure translation on top of the follow pose (zero under
    // reduce motion, VFX below Full, capture mode or an automated browser).
    // Only on a frame the follow pose was re-set: the offset must never accumulate.
    const shake = this.impact.shake(
      now,
      dt,
      !dungeonBlocksInput && shakeAllowed(readFlag(CAPTURE_KEYS.captureMode)),
    );
    if (shake.x !== 0 || shake.y !== 0 || shake.z !== 0)
      this.orbit.camera.position.add(new THREE.Vector3(shake.x, shake.y, shake.z));
    // WR.3.4: distance-cull far scatter against the camera (frustum cull is automatic).
    this.sceneBuilder.updateView(this.orbit.camera.position);
    // VQ-001 (AD-194): the post-processing stack owns the render call — the full
    // composer chain on desktop, a direct AgX-tone-mapped render when degraded.
    // AP5.S1: present each rigged player's animation state, then advance every
    // mixer once. Self is driven from the PREDICTED movement (so the local avatar
    // animates without waiting for a server round-trip); remotes from the `anim`
    // field already on the wire. `setAnimState` is a no-op for a repeated state
    // and for any entity with no rig, so this stays cheap at 20-player scale.
    // Hard-guarded: this block sits BEFORE the render call, so an unexpected throw
    // here would black-screen the whole game. Presentation must never cost a frame
    // — on any failure we drop animation for this frame and still render.
    // q-combat-game-feel hit stop: the own rig is keyed OWN_RIG_KEY, not its entity id.
    const heldRig = (key: number): boolean =>
      this.impact.isHeld(key === OWN_RIG_KEY ? this.ownEntityId : key, now);
    if (
      this.playerRigRenderer !== null ||
      this.npcPlacer !== null ||
      this.creatureRenderer !== null
    ) {
      try {
        if (this.playerRigRenderer !== null) {
          // Speed-matched locomotion: each rig also gets its RENDERED position, so its
          // run/walk plays at the rate that keeps the feet planted (after setAnimState).
          if (this.ownEntityId >= 0) {
            this.playerRigRenderer.setAnimState(OWN_RIG_KEY, animFor(this.predicted));
            if (this.ownMesh !== null) {
              const own = this.ownMesh.position;
              this.playerRigRenderer.observeGround(OWN_RIG_KEY, own.x, own.z, dt);
            }
          }
          for (const entity of this.entities.values()) {
            this.playerRigRenderer.setAnimState(entity.latest.id, entity.latest.anim);
            const at = entity.mesh.position;
            this.playerRigRenderer.observeGround(entity.latest.id, at.x, at.z, dt);
          }
          this.playerRigRenderer.update(dt, this.impact.anyHeld(now) ? heldRig : undefined);
        }
        // AP5.S2 + q-npc-ambient-animations: advance the placed NPCs' ambient sets (a no-op
        // without a mixer factory). The own avatar's rendered (x, z) lets an NPC greet, talk
        // and turn as the player walks up — client-side only, nothing on the wire.
        const observer =
          this.ownMesh !== null ? { x: this.ownMesh.position.x, z: this.ownMesh.position.z } : null;
        this.npcPlacer?.update(dt, observer);
        this.npcPlacer?.faceMarkers(this.orbit.yaw); // AD-867: the "?" faces the view
        // AD-665: the own gather channel's progress fills its node's ring inward.
        const channel = this.rig?.moments?.gather.get() as
          | { nodeId?: number; startedAtMs?: number; durationMs?: number }
          | null
          | undefined;
        if (
          channel != null &&
          channel.nodeId !== undefined &&
          channel.startedAtMs !== undefined &&
          channel.durationMs !== undefined &&
          channel.durationMs > 0
        ) {
          const frac = Math.min(1, Math.max(0, (now - channel.startedAtMs) / channel.durationMs));
          this.nodePlacer?.setGatherProgress(channel.nodeId, frac);
        } else {
          this.nodePlacer?.setGatherProgress(null, null);
        }
        // Enemy proxies: wire anim → Idle/Walk (a no-op for static proxies), then advance
        // their mixers and age any lingering corpses.
        if (this.creatureRenderer !== null) {
          for (const entity of this.entities.values()) {
            if (entity.latest.kind === 'enemy') {
              this.creatureRenderer.setAnimState(entity.latest.id, entity.latest.anim);
              const at = entity.mesh.position;
              this.creatureRenderer.observeGround(entity.latest.id, at.x, at.z, dt);
            }
          }
          this.creatureRenderer.update(dt, this.impact.anyHeld(now) ? heldRig : undefined);
        }
      } catch (error) {
        if (!this.animWarned) {
          this.animWarned = true; // once, not every frame
          console.warn('[rig-anim] frame update failed; rendering continues', error);
        }
      }
    }

    // q-npc-props-adoption-and-flame: the flames' clock, fog fade and light pool (placed
    // on the nearest lit flames drawn last frame).
    this.flames?.update(dt, this.orbit.camera, this.scene.fog);
    this.vfxFrame(now, dt);
    this.renderer.info.reset();
    this.postfx.render(this.scene, this.orbit.camera, dt);
    this.firstFrameGate?.frameRendered(now);
    this.sampleReadiness(now);
    if (this.sceneDumpHook) {
      this.lastFrameRenderInfo = snapshotRenderInfo(
        this.renderer.info as unknown as RendererInfoLike,
      );
    }

    // HUD status (throttled).
    this.status.cameraYaw = this.orbit.yaw;
    this.status.dodgeReadyPct =
      this.predicted.dodgeCooldownMs <= 0
        ? 1
        : 1 - this.predicted.dodgeCooldownMs / DODGE_COOLDOWN_MS;
    if (this.status.flash && now > this.flashUntilMs) this.status.flash = null;
    this.pushStatus(false);
  }

  /**
   * AD-794: feed the loading screen's latch, every READINESS_SAMPLE_MS after a rendered
   * frame, until it latches. In view = the camera frustum out to the draw range (far plane
   * or fog). Permanent fallbacks (an enemy with no model, a rig that cannot build once the
   * pack is in) are reported, never waited on. Presentation-only: a throw here must never
   * cost the frame, so the screen then falls back to its timeout.
   */
  private sampleReadiness(now: number): void {
    const session = this.readiness;
    if (session === undefined || session.ready) return;
    if (now - this.lastReadinessSampleMs < READINESS_SAMPLE_MS) return;
    this.lastReadinessSampleMs = now;
    try {
      const camera = this.orbit.camera;
      const view = viewVolume(camera, drawRange(camera, this.scene.fog));
      const packIn = this.contentPackState !== 'pending';
      const exclude = new Set<THREE.Object3D>();
      const fallbacks: string[] = [];
      const entityByGroup = new Map<THREE.Object3D, number>();
      for (const [id, entity] of this.entities) {
        entityByGroup.set(entity.mesh, id);
        const isFallback =
          entity.latest.kind === 'enemy'
            ? this.creatureRenderer?.isFallback(id) === true
            : packIn && this.playerRigRenderer?.isFallback(id) === true;
        if (!isFallback) continue;
        exclude.add(entity.mesh);
        fallbacks.push(`${entity.latest.kind}: ${entity.latest.name ?? id}`);
      }
      if (this.ownMesh && packIn && this.playerRigRenderer?.isFallback(OWN_RIG_KEY) === true) {
        exclude.add(this.ownMesh);
        fallbacks.push('your character (capsule)');
      }
      const bodies = placeholderBodiesInView(this.scene, view, this.ownMesh, exclude).map(
        (body) => {
          if (body.kind === 'own') return { key: 'body:own', label: 'your character' };
          const id = entityByGroup.get(body.group);
          const name = id === undefined ? undefined : this.entities.get(id)?.latest.name;
          return {
            key: `body:${id ?? `g${body.group.id}`}`,
            label: `${body.kind === 'enemy' ? 'creature' : 'player'}: ${name ?? 'unnamed'}`,
          };
        },
      );
      const loads = this.pendingAssets
        .entries()
        .filter((entry) => anchorInView(entry.anchor, view))
        .map((entry) => ({ key: entry.key, label: entry.label }));
      const verdict = evaluateReadiness({
        worldApplied: this.lastWorld !== null,
        contentPack: this.contentPackState,
        ownAvatar: this.ownMesh !== null,
        bodiesInView: bodies,
        loadsInView: loads,
      });
      session.observe(now, verdict, fallbacks);
      if (session.ready) this.assetUpgradeGate?.release(now); // the textured bases may load now
    } catch (error) {
      if (!this.readinessWarned) {
        this.readinessWarned = true;
        console.warn('[readiness] sample failed; the loading screen keeps its timeout', error);
      }
    }
  }

  private pushStatus(force: boolean): void {
    const now = performance.now();
    if (!force && now - this.lastStatusPushMs < 150) return;
    this.lastStatusPushMs = now;
    this.onStatus({ ...this.status });
  }

  /**
   * Release a SUCCESSFULLY constructed instance.
   *
   * The step ORDER is unchanged (AD-195 / O-003 stay closed); what changed in
   * H-258 is that every step is now guarded on its own. This body used to be a
   * bare 20-statement sequence with a single guarded region, so a throw at the
   * first teardown — `dungeonLayer.dispose()` — stranded the listeners, the dev
   * bridge, the three renderer sub-objects, the composer targets, the renderer
   * and the canvas. That is the release path a healthy instance takes, i.e. the
   * one holding a full set of resources to strand; the constructor rollback,
   * which already guarded, is the rarer of the two.
   *
   * `runTeardown` runs every step and rethrows the FIRST failure afterwards, so
   * a caller that would have seen an error still sees one.
   */
  dispose(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);

    const canvas = this.renderer.domElement;
    runTeardown([
      // First: forceContextLoss() below must not read as a renderer loss.
      () => this.detachLossWatch(),
      () => {
        this.rendererLostListener = null;
      },
      () => {
        this.dungeonLayer?.dispose(); // O-025: shrines + explorer + DOM prompts
        this.dungeonLayer = null;
      },
      () => {
        this.dungeonEntrances?.dispose(); // server entrance shrines (no DOM, no listeners)
        this.dungeonEntrances = null;
      },
      () => {
        this.dungeonRunClient?.dispose(); // slice 2d: G listener + prompt
        this.dungeonRunClient = null;
      },
      () => {
        this.dungeonRunScene?.release(); // slice 2e: interior disposed, world roots restored
        this.dungeonRunScene = null;
      },
      // O-026: drop the dev bridge so a remount never sees a stale layer.
      () => setDevBridge(null),
      () => this.dungeonPackLoaders?.clear(),
      () => window.removeEventListener('resize', this.onResize),
      () => this.detachTapControls(),
      () => this.explicit.clear(), // AD-817: a remount never inherits an old id space's target
      () => this.orbit.dispose(),
      () => this.input.dispose(),
      () => this.teardownEquipment(), // E2.2.S2: unsubscribe + dispose self-view equipment
      () => {
        for (const id of [...this.remoteEquipment.keys()]) this.dropRemoteEquipment(id); // AD-671
      },
      () => {
        this.creatureRenderer?.dispose(); // E2.3.S2: dispose enemy proxies + loader cache
        this.creatureRenderer = null;
      },
      () => {
        this.playerRigRenderer?.dispose(); // E2.4.S2: dispose player rigs + loader cache
        this.playerRigRenderer = null;
        this.ownRigRoot = null;
      },
      () => {
        this.npcPlacer?.dispose(); // E2.4.S3: dispose placed NPC meshes + loader caches
        this.npcPlacer = null;
        this.nodePlacer?.dispose(); // E3.4.S3: dispose placed node meshes + loader caches
        this.nodePlacer = null;
      },
      // Detach the DEV scene-dump hook on the "only if it is still ours" terms the
      // constructor rollback uses — one implementation, so the two cannot disagree.
      () => this.detachSceneDumpHook(),
      // The ctor acquisitions with no step above (H-1349), each guarded on its own;
      // then drop both ledgers so a disposed instance holds no teardown closures.
      () => {
        const lifetime = [...this.lifetimeCleanup].reverse();
        this.lifetimeCleanup = [];
        this.ctorCleanup = [];
        runTeardown(lifetime);
      },
      () => this.sceneBuilder.clear(),
      () => this.postfx.dispose(), // free composer targets + pass resources (VQ-001)
      // Fully release the WebGL context — not just its GPU resources. The WebGPU
      // renderer has no forceContextLoss(), so guard that WebGL-only cleanup while
      // retaining O-003's deterministic teardown on the default path.
      () => {
        const maybeWebGl = this.renderer as { forceContextLoss?: () => void };
        if (typeof maybeWebGl.forceContextLoss !== 'function') return;
        try {
          maybeWebGl.forceContextLoss();
        } catch {
          /* context already lost — nothing to release */
        }
      },
      () => this.renderer.dispose(),
      () => canvas.remove(),
    ]);
  }
}
